import { create } from 'zustand';
import {
  Project,
  Rack,
  PlacedEquipment,
  Cable,
  Equipment,
  Design,
  FloorplanData,
  CanvasMode,
  WallDragMode,
  DesignFloorPlan,
  Wall,
  Opening,
  Room,
  FloorPlanReferenceImage,
  BrowserItemKind,
  BrowserGroupKey,
  CanvasBox,
  CanvasLabel,
  CableRouting,
} from '../types';
import {
  createEmptyFloorPlan,
  migrateFloorplanDataToDesignFloorPlan,
  DEFAULT_DOOR_WIDTH_M,
  DEFAULT_PIXELS_PER_METER,
  mergeWallGeometry,
  remapOpeningParameter,
  splitWallAtTJunctions,
  remapOpeningsForDrag,
  rebuildRoomPolygons,
  applyDeletedWallsToRooms,
  normalizeFloorPlanReferenceImages,
  getFloorPlanReferenceImages,
} from '../utils/floorPlanGeometry';
import {
  isItemEffectivelyVisible,
  withBrowserGroupVisible,
  withEquipmentTypeVisible,
} from '../utils/browserVisibility';
import { EditableEquipment } from '../types/portCapabilities';
import { applyAppUIFont, type AppUIFont } from '../utils/appUIFont';
import { uniqueCablesById, findCableBetween, upsertCable } from '../utils/cableRuns';
import { useAuthStore } from './authStore';
import { useCanvasCollabStore, type CanvasCollabMode } from './canvasCollabStore';
import {
  registerCanvasCollabRuntime,
  requestCanvasCommit,
  beginCollabWrite,
  getCanvasSaveGeneration,
  rememberDesignIds,
  removedDesignIds,
  mergeById,
  getLastLocalCanvasMutationAt,
} from '../lib/realtime/canvasCollab';
import { restartCollectionRealtime } from '../lib/realtime/collectionSync';
import {
  writeProjectWorkingCopy,
  readProjectWorkingCopy,
  workingCopyIsNewer,
  clearProjectWorkingCopy,
} from '../lib/projectWorkingCopy';
import {
  fetchCollectionsForUser,
  fetchDesignCountsByCollection,
  saveCollectionProject,
  loadCollectionAsProject,
  createCollectionRecord,
  insertDesignRecord,
  deleteCollectionRecord,
  renameCollectionRecord,
  setLastCollectionId,
  collectionToProject,
  designSettingsFromRow,
} from '../lib/collectionPersistence';
import {
  fetchFloorplansForCollection,
  upsertFloorplanRecord,
  deleteFloorplanRecord,
  dbRowToFloorplan,
  migrateLocalFloorplans,
  overlayPublicUrl,
} from '../lib/floorplanPersistence';
import { getActiveCollectionId, switchCollectionById, setCollectionPartialState } from './collectionRef';
import {
  accountUiPrefsFromUnknown,
  mergeAccountUiPrefs,
  persistAccountUiPrefs,
  readSavedAccountUiPrefs,
  resolveAccountUiPrefs,
  settingsForNewCollection,
  type AccountUiPrefs,
  type SnapToGridTarget,
} from '../utils/accountUiPrefs';
import { DEFAULT_ICON_GRID_CELLS, parseIconGridCells, type IconGridCells } from '../utils/canvasIcon';
import { rewritePortEndpoint, buildPortNumberRemaps } from '../utils/portNamingSchemes';
import { powerCableGuard } from '../utils/powerCableGuard';
import { remapPortCustomLabels } from '../utils/portCustomLabels';
import {
  clampCanvasZoom,
  readCanvasCamera,
  readCanvasCameraSession,
  writeCanvasCamera,
  writeCanvasCameraSession,
} from '../utils/canvasCameraPrefs';

// Simple UUID generator for compatibility
const generateId = () => {
  return 'xxxx-xxxx-xxxx'.replace(/[x]/g, () => {
    return (Math.random() * 16 | 0).toString(16);
  });
};

// Generate unique equipment name with auto-numbering
const generateUniqueEquipmentName = (baseName: string, existingEquipment: PlacedEquipment[]): string => {
  // Get all existing names
  const existingNames = existingEquipment.map(eq => eq.equipment.name);
  
  // Count all equipment that have this base name (including those with auto-numbering)
  // This includes both "Desktop Computer" and "Desktop Computer (2)", "Desktop Computer (3)", etc.
  const baseNameMatches = existingEquipment.filter(eq => {
    const cleanName = eq.equipment.name.replace(/ \(\d+\)$/, '');
    return cleanName === baseName;
  });
  
  const totalCount = baseNameMatches.length;
  
  if (totalCount === 0) {
    return baseName;
  }
  
  // Find the next available number
  let counter = totalCount + 1;
  let uniqueName = `${baseName} (${counter})`;
  
  // Make sure the name doesn't already exist
  while (existingNames.includes(uniqueName)) {
    counter++;
    uniqueName = `${baseName} (${counter})`;
  }
  
  return uniqueName;
};

// Design storage utilities (renamed from Project)
const STORAGE_KEYS = {
  PROJECTS: 'netprobuilder_designs', // Changed from 'netprobuilder_projects'
  LAST_PROJECT: 'netprobuilder_last_design' // Changed from 'netprobuilder_last_project'
};

const saveProjectToStorage = (project: Project) => {
  try {
    const projects = JSON.parse(localStorage.getItem(STORAGE_KEYS.PROJECTS) || '{}');
    projects[project.id] = {
      ...project,
      createdAt: project.createdAt.toISOString(),
      updatedAt: project.updatedAt.toISOString()
    };
    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    localStorage.setItem(STORAGE_KEYS.LAST_PROJECT, project.id);
    console.log('Design saved:', project.name);
  } catch (error) {
    console.error('Failed to save design:', error);
  }
};

const loadProjectFromStorage = (projectId: string): Project | null => {
  try {
    const projects = JSON.parse(localStorage.getItem(STORAGE_KEYS.PROJECTS) || '{}');
    const projectData = projects[projectId];
    if (projectData) {
      return {
        ...projectData,
        createdAt: new Date(projectData.createdAt),
        updatedAt: new Date(projectData.updatedAt)
      };
    }
  } catch (error) {
    console.error('Failed to load design:', error);
  }
  return null;
};

const getLastProjectId = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.LAST_PROJECT);
};

const getAllProjects = (): Project[] => {
  try {
    const projects = JSON.parse(localStorage.getItem(STORAGE_KEYS.PROJECTS) || '{}');
    return Object.values(projects).map((projectData: any) => ({
      ...projectData,
      createdAt: new Date(projectData.createdAt),
      updatedAt: new Date(projectData.updatedAt)
    }));
  } catch (error) {
    console.error('Failed to load designs:', error);
    return [];
  }
};

/** Resolve canvas position for an equipment/rack id (supports `id:port:N`) */
const resolveNodePosition = (
  design: Design,
  nodeId: string
): { x: number; y: number } | null => {
  const baseId = nodeId.includes(':port:') ? nodeId.split(':port:')[0] : nodeId;
  const equipment = (design.equipment || []).find((e) => e.id === baseId);
  if (equipment) return equipment.position;
  const rack = (design.racks || []).find((r) => r.id === baseId);
  if (rack) return rack.position;
  return null;
};

/** Estimate cable length in meters from straight-line distance using floor plan scale */
const estimateCableLengthMeters = (design: Design, fromId: string, toId: string): number | undefined => {
  const from = resolveNodePosition(design, fromId);
  const to = resolveNodePosition(design, toId);
  if (!from || !to) return undefined;
  const px = Math.hypot(to.x - from.x, to.y - from.y);
  const ppm = DEFAULT_PIXELS_PER_METER;
  if (ppm <= 0 || px <= 0) return undefined;
  // Round to 1 decimal meter
  return Math.round((px / ppm) * 10) / 10;
};

// Helper: ensure a project has at least one Design entry that mirrors
// the legacy single-design-per-project structure. This prepares us for
// true multi-design support while remaining backward compatible.
const ensureProjectHasDesigns = (project: Project): Project => {
  if (project.designs && project.designs.length > 0) {
    return project;
  }

  const baseDesign: Design = {
    id: project.id,
    name: project.name,
    createdAt: project.createdAt,
    updatedAt: project.updatedAt,
    racks: project.racks,
    equipment: project.equipment,
    cables: project.cables,
    logicMode: 'plan',
  };

  // IMPORTANT: once we have a design array, treat it as the single
  // source of truth for racks/equipment/cables and clear the legacy
  // project-level collections so nothing else accidentally reads them.
  return {
    ...project,
    racks: [],
    equipment: [],
    cables: [],
    designs: [baseDesign],
  };
};

const writeDesignCables = (
  project: Project,
  designId: string,
  cables: Cable[]
): Project => {
  const unique = uniqueCablesById(cables);
  const updatedDesigns = (project.designs || []).map((d) =>
    d.id === designId ? { ...d, cables: unique, updatedAt: new Date() } : d
  );
  return {
    ...project,
    designs: updatedDesigns,
    updatedAt: new Date(),
  };
};

const deleteProjectFromStorage = (projectId: string) => {
  try {
    const projects = JSON.parse(localStorage.getItem(STORAGE_KEYS.PROJECTS) || '{}');
    delete projects[projectId];
    localStorage.setItem(STORAGE_KEYS.PROJECTS, JSON.stringify(projects));
    
    // Clear last design if it was deleted
    const lastProjectId = getLastProjectId();
    if (lastProjectId === projectId) {
      localStorage.removeItem(STORAGE_KEYS.LAST_PROJECT);
    }
  } catch (error) {
    console.error('Failed to delete design:', error);
  }
};

interface ProjectState {
  currentProject: Project | null;
  // Logical design selection within the current project. For now this will
  // default to the legacy single-design model, but it prepares us for multiple
  // designs per project later.
  currentDesignId: string | null;
  availableProjects: Project[];
  isProjectListOpen: boolean;
  lastSaved: Date | null;
  isAutoSaving: boolean;
  autosaveSettings: {
    enabled: boolean;
    intervalMinutes: number;
  };
  featureFlags: {
    enableCustomTemplates: boolean;
    showBetaBadges: boolean;
    enableAdvancedCableRun: boolean;
  };
  canvasCollabMode: CanvasCollabMode;
  selectedRack: Rack | null;
  selectedEquipment: PlacedEquipment | null;
  selectedRackEquipment: { rackId: string; equipment: any; index: number } | null;
  selectedCable: Cable | null;
  /** Cable id created in this session and not yet confirmed in CablePortEditor. Close keeps it; Delete removes it. */
  pendingNewCableId: string | null;
  isCablePortEditorOpen: boolean;
  isRackConfigOpen: boolean;
  isEquipmentConfigOpen: boolean;
  isCableConfigOpen: boolean;
  isCableEditModalOpen: boolean;
  isSettingsConfigOpen: boolean;
  isSettingsPinned: boolean;
  isAddEquipmentOpen: boolean;
  isRackBuilderOpen: boolean;
  isFloorplanDrawerOpen: boolean;
  isFloorplanSelectionOpen: boolean;
  showAPRange: boolean;
  draggedItem: any | null;
  isCableRunMode: boolean;
  cableRunRouting: CableRouting;
  selectedSourceEquipment: PlacedEquipment | null;
  hoveredCableId: string | null;
  hoveredEquipmentId: string | null;
  hoveredPortIdDuringCableDrag: string | null;
  // Canvas panning state
  canvasPanX: number;
  canvasPanY: number;
  canvasZoom: number; // Zoom level (0.5, 0.75, 1, 1.5, 2)
  // Floorplan panning state
  floorplanPanX: number;
  floorplanPanY: number;
  draggedEquipmentPositions: Record<string, { x: number; y: number }>;
  isEquipmentDragActive: boolean;
  isRackPlacementMode: boolean;
  pendingRackPlacement: {
    size: number;
    equipment: any[];
    name: string;
    siteId?: string | null;
  } | null;
  snapToGrid: boolean;
  snapToGridTarget: SnapToGridTarget;
  gridSize: number;
  iconGridCells: IconGridCells;
  showGrid: boolean;
  showLegend: boolean;
  isExistingMode: boolean;
  alwaysShowExpandedEquipment: boolean;
  portsDisplayMode: 'none' | 'full';
  badgeAnchor: 'top-left' | 'top-right' | 'center-top' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'center-bottom';
  badgeShape: 'square' | 'circle';
  canvasBackground: 'light' | 'dark';
  panelVisualization: 'shaded' | 'minimalist';
  wallDragMode: WallDragMode;
  currency: 'USD' | 'ZAR' | 'GBP';
  appTheme: 'default' | 'dark';
  appUIFont: AppUIFont;
  showCanvasToolbar: boolean;
  showDesignInfoOverlay: boolean;
  showMinimap: boolean;
  showManufacturerInEquipmentList: boolean;
  expandBadgesOnHover: boolean;
  colourizePorts: boolean;
  equipmentPanelAdvancedDefault: boolean;
  equipmentPanelPreviewHidden: boolean;
  floorPlanUnits: 'metric' | 'imperial';

  // Tab management for multi-canvas support
  openDesigns: string[]; // Array of design IDs that are open in tabs
  activeDesignId: string | null; // Currently active/selected design tab
  openFloorplans: string[]; // Array of floorplan IDs that are open in tabs
  activeFloorplanId: string | null; // Currently active/selected floorplan tab

  /** Unified designer mode: select / wall / room / door / measure / place */
  canvasMode: CanvasMode;
  selectedFloorPlanWallId: string | null;
  selectedFloorPlanWallIds: string[];
  selectedFloorPlanRoomId: string | null;
  selectedFloorPlanOpeningId: string | null;
  selectedReferenceImageId: string | null;
  /** Images waiting for a canvas click to place (like equipment placement). */
  pendingReferenceImages: Array<Omit<FloorPlanReferenceImage, 'id' | 'x' | 'y'> & { id?: string }>;
  floorPlanHistoryPast: DesignFloorPlan[];
  floorPlanHistoryFuture: DesignFloorPlan[];
  /** Wall id awaiting first-time scale calibration prompt */
  pendingScaleWallId: string | null;

  // Adapter helpers for future multi-design support
  getCurrentDesign: () => Design | null;
  getDesignsForCurrentProject: () => Design[];
  setCurrentDesignId: (designId: string) => void;
  createDesign: (name: string) => void;
  renameDesign: (designId: string, name: string) => void;
  deleteDesign: (designId: string) => void;
  
  // Tab management methods
  openDesignTab: (designId: string) => void;
  closeDesignTab: (designId: string) => void;
  setActiveDesignTab: (designId: string) => void;
  getOpenDesigns: () => Design[];
  
  // Floorplan tab management methods
  openFloorplanTab: (floorplanId: string) => void;
  closeFloorplanTab: (floorplanId: string) => void;
  setActiveFloorplanTab: (floorplanId: string) => void;
  getOpenFloorplans: () => FloorplanData[];
  // Adapters for future floorplan and site aggregation
  getFloorplansForCurrentProject: () => FloorplanData[];
  getSitesForCurrentProject: () => any[];

  // Actions
  createProject: (name: string, customerName: string, address: string) => void;
  saveProject: () => void;
  persistWorkingCopy: (immediate?: boolean) => void;
  saveProjectForCollab: (generation: number) => Promise<void>;
  applyRemoteDesign: (event: 'INSERT' | 'UPDATE' | 'DELETE', row: Record<string, unknown>) => void;
  setCanvasCollabMode: (mode: CanvasCollabMode) => void;
  loadProject: (projectId: string) => void;
  loadLastProject: () => void;
  deleteProject: (projectId: string) => void;
  renameProject: (projectId: string, name: string) => void;
  refreshAvailableProjects: () => void;
  refreshAvailableProjectsFromCloud: (userId: string) => Promise<void>;
  hydrateProject: (project: Project) => void;
  applyAccountUiPrefs: (prefs: AccountUiPrefs) => void;
  setProjectListOpen: (open: boolean) => void;
  addRack: (size: number, position: { x: number; y: number }, equipment?: any[], name?: string, siteId?: string | null) => void;
  removeRack: (rackId: string) => void;
  updateRack: (rackId: string, updates: Partial<Rack>) => void;
  updateRackPosition: (rackId: string, position: { x: number; y: number }) => void;
  addEquipmentToRack: (rackId: string, equipment: Equipment, startUnit: number) => void;
  moveRackEquipment: (rackId: string, fromIndex: number, toStartUnit: number) => void;
  updateRackEquipmentConfig: (rackId: string, equipmentIndex: number, config: any) => void;
  addEquipmentToCanvas: (equipment: Equipment, position: { x: number; y: number }) => void;
  duplicatePlacedEquipment: (equipmentId: string) => void;
  updateEquipmentPosition: (equipmentId: string, position: { x: number; y: number }) => void;
  updateEquipmentConfig: (equipmentId: string, config: any, options?: { persistToLibrary?: boolean }) => void;
  patchCurrentDesign: (patch: Partial<Design>) => void;
  updateProjectCustomer: (customer: any) => void;
  removeEquipment: (equipmentId: string) => void;
  replaceEquipment: (placedEquipmentId: string, newEquipment: Equipment) => void;
  setEquipmentLabel: (equipmentId: string, label: string) => void;
  addCable: (from: string, to: string, type: 'ethernet' | 'fiber' | 'power') => void;
  addCableWithProperties: (cable: Cable) => void;
  setSelectedRack: (rack: Rack | null) => void;
  setSelectedEquipment: (equipment: PlacedEquipment | null) => void;
  setSelectedRackEquipment: (rackId: string, rackEquipment: any | null) => void;
  setSelectedCable: (cable: Cable | null) => void;
  setCablePortEditorOpen: (open: boolean) => void;
  setRackConfigOpen: (open: boolean) => void;
  setEquipmentConfigOpen: (open: boolean) => void;
  setCableConfigOpen: (open: boolean) => void;
  setCableEditModalOpen: (open: boolean) => void;
  setSettingsConfigOpen: (open: boolean) => void;
  setIsSettingsPinned: (pinned: boolean) => void;
  setFloorplanDrawerOpen: (open: boolean) => void;
  setFloorplanSelectionOpen: (open: boolean) => void;
  setAddEquipmentOpen: (open: boolean) => void;
  setRackBuilderOpen: (open: boolean) => void;
  setShowAPRange: (show: boolean) => void;
  setDraggedItem: (item: any) => void;
  setFloorplan: (url: string, width: number, height: number) => void;
  setFloorplanData: (data: any) => void;
  clearFloorplan: () => void;

  // Unified floor plan (on Design)
  setCanvasMode: (mode: CanvasMode) => void;
  getFloorPlan: () => DesignFloorPlan | null;
  ensureFloorPlan: () => DesignFloorPlan;
  updateFloorPlan: (updater: (fp: DesignFloorPlan) => DesignFloorPlan, pushHistory?: boolean) => void;
  snapshotFloorPlanForDrag: () => void;
  applyFloorPlanLive: (next: DesignFloorPlan) => void;
  discardLastFloorPlanSnapshot: () => void;
  addWall: (wall: Omit<Wall, 'id'> & { id?: string }) => string;
  updateWall: (wallId: string, updates: Partial<Wall>) => void;
  deleteWall: (wallId: string) => void;
  deleteWalls: (wallIds: string[]) => void;
  mergeWalls: (idA: string, idB: string) => { ok: true } | { ok: false; reason: string };
  splitWall: (wallId: string) => { ok: true } | { ok: false; reason: string };
  addOpening: (opening: Omit<Opening, 'id'> & { id?: string }) => string;
  updateOpening: (openingId: string, updates: Partial<Opening>) => void;
  deleteOpening: (openingId: string) => void;
  addRoom: (room: Omit<Room, 'id'> & { id?: string }) => string;
  updateRoom: (roomId: string, updates: Partial<Room>) => void;
  deleteRoom: (roomId: string) => void;
  setFloorPlanUnits: (units: 'metric' | 'imperial') => void;
  setFloorPlanScale: (pixelsPerMeter: number, calibrated?: boolean) => void;
  calibrateFloorPlanFromWall: (wallId: string, lengthValue: number, unit: 'm' | 'cm' | 'ft' | 'in') => void;
  setPendingScaleWallId: (wallId: string | null) => void;
  setSelectedFloorPlanWallId: (wallId: string | null) => void;
  selectFloorPlanWall: (wallId: string | null, additive?: boolean) => void;
  setSelectedFloorPlanRoomId: (roomId: string | null) => void;
  setSelectedFloorPlanOpeningId: (openingId: string | null) => void;
  setSelectedReferenceImageId: (imageId: string | null) => void;
  queueFloorPlanReferenceImages: (
    images: Array<Omit<FloorPlanReferenceImage, 'id' | 'x' | 'y'> & { id?: string }>
  ) => void;
  placeNextPendingReferenceImage: (position: { x: number; y: number }) => string | null;
  cancelPendingReferenceImages: () => void;
  addFloorPlanReferenceImages: (
    images: Array<Omit<FloorPlanReferenceImage, 'id'> & { id?: string }>
  ) => string[];
  updateFloorPlanReferenceImage: (
    id: string,
    patch: Partial<Omit<FloorPlanReferenceImage, 'id' | 'url'>>,
    pushHistory?: boolean
  ) => void;
  removeFloorPlanReferenceImage: (id: string) => void;
  clearFloorPlanReferenceImages: () => void;
  /** @deprecated Prefer clearFloorPlanReferenceImages / queueFloorPlanReferenceImages */
  setFloorPlanReferenceImage: (image: DesignFloorPlan['referenceImage'] | null) => void;
  setBrowserGroupVisible: (group: BrowserGroupKey, visible: boolean) => void;
  setBrowserEquipmentTypeVisible: (typeKey: string, visible: boolean) => void;
  setEquipmentPanelPreviewHidden: (hidden: boolean) => void;
  setItemHidden: (kind: BrowserItemKind, id: string, hidden: boolean) => void;
  isItemVisible: (kind: BrowserItemKind, id: string) => boolean;
  undoFloorPlan: () => void;
  redoFloorPlan: () => void;
  getPixelsPerMeter: () => number;
  canvasDistanceToMeters: (px: number) => number;
  migrateLegacyFloorplanIntoDesign: () => boolean;
  cachedFloorplans: (FloorplanData & { _dbId?: string; overlayPath?: string | null; overlayUrl?: string })[];
  getUserPreferences: () => any;
  setUserPreferences: (preferences: any) => void;
  saveFloorplan: (floorplan: any) => void;
  loadFloorplan: (floorplanId: string) => FloorplanData | null;
  deleteFloorplan: (floorplanId: string) => void;
  getFloorplans: () => any[];
  fetchAndCacheFloorplans: (collectionId: string) => Promise<void>;
  setCableRunMode: (active: boolean) => void;
  setCableRunRouting: (routing: CableRouting) => void;
  setSelectedSourceEquipment: (equipment: PlacedEquipment | null) => void;
  setHoveredCableId: (cableId: string | null) => void;
  setHoveredEquipmentId: (equipmentId: string | null) => void;
  setHoveredPortIdDuringCableDrag: (portId: string | null) => void;
  setCanvasPan: (panX: number, panY: number) => void;
  setCanvasZoom: (zoom: number) => void;
  setFloorplanPan: (panX: number, panY: number) => void;
  createCableRun: (fromId: string, toId: string, type: 'ethernet' | 'fiber' | 'power', label?: string, subtype?: string) => void;
  confirmPendingCable: () => void;
  discardPendingCable: () => void;
  updateCableRun: (cableId: string, updates: Partial<Cable>) => void;
  setAllCableRouting: (routing: CableRouting) => number;
  remapPlacedEquipmentPortNumbers: (
    placedId: string,
    remaps: Array<{ face?: string; map: Record<number, number>; endpoints?: Record<string, string> }>,
    options?: { nextLayout?: any; detachOrphans?: boolean }
  ) => void;
  deleteCableRun: (cableId: string) => void;
  clearAllCableRuns: () => void;
  setDraggedEquipmentPosition: (equipmentId: string, position: { x: number; y: number }) => void;
  clearDraggedEquipmentPosition: (equipmentId: string) => void;
  setEquipmentDragActive: (active: boolean) => void;
  setRackPlacementMode: (active: boolean, pendingRack?: { size: number; equipment: any[]; name: string; siteId?: string | null }) => void;
  placeRackAtPosition: (position: { x: number; y: number }) => void;
  setSnapToGrid: (enabled: boolean) => void;
  setSnapToGridTarget: (target: SnapToGridTarget) => void;
  setGridSize: (size: number) => void;
  setIconGridCells: (cells: IconGridCells) => void;
  setShowGrid: (show: boolean) => void;
  setShowLegend: (show: boolean) => void;
  setAlwaysShowExpandedEquipment: (show: boolean) => void;
  setPortsDisplayMode: (mode: 'none' | 'full') => void;
  setBadgeAnchor: (anchor: 'top-left' | 'top-right' | 'center-top' | 'left' | 'right' | 'bottom-left' | 'bottom-right' | 'center-bottom') => void;
  setBadgeShape: (shape: 'square' | 'circle') => void;
  setCanvasBackground: (mode: 'light' | 'dark') => void;
  setPanelVisualization: (mode: 'shaded' | 'minimalist') => void;
  setWallDragMode: (mode: WallDragMode) => void;
  setCurrency: (currency: 'USD' | 'ZAR' | 'GBP') => void;
  setAppTheme: (theme: 'default' | 'dark') => void;
  setAppUIFont: (font: AppUIFont) => void;
  setShowCanvasToolbar: (show: boolean) => void;
  setShowDesignInfoOverlay: (show: boolean) => void;
  setShowMinimap: (show: boolean) => void;
  setShowManufacturerInEquipmentList: (show: boolean) => void;
  setExpandBadgesOnHover: (show: boolean) => void;
  setColourizePorts: (enabled: boolean) => void;
  setEquipmentPanelAdvancedDefault: (enabled: boolean) => void;
  
  // Existing infrastructure management
  toggleEquipmentExistingStatus: (equipmentId: string) => void;
  toggleRackExistingStatus: (rackId: string) => void;
  toggleCableExistingStatus: (cableId: string) => void;
  setEquipmentExistingStatus: (equipmentId: string, isExisting: boolean) => void;
  setRackExistingStatus: (rackId: string, isExisting: boolean) => void;
  setCableExistingStatus: (cableId: string, isExisting: boolean) => void;
  setExistingMode: (enabled: boolean) => void;
  
  // Port system state
  selectedEquipmentForPortView: PlacedEquipment | null;
  isPortVisualizationOpen: boolean;
  selectedEquipmentForEdit: EditableEquipment | null;
  isEquipmentEditorOpen: boolean;
  equipmentModifications: Record<string, EditableEquipment>;
  
  // Equipment customizations (permanent configs per equipment type, including layouts)
  equipmentCustomizations: Record<string, { config: any }>;
  
  // Port system actions
  setSelectedEquipmentForPortView: (equipment: PlacedEquipment | null) => void;
  setPortVisualizationOpen: (open: boolean) => void;
  setSelectedEquipmentForEdit: (equipment: EditableEquipment | null) => void;
  setEquipmentEditorOpen: (open: boolean) => void;
  saveEquipmentModifications: (equipment: EditableEquipment) => void;
  resetEquipmentModifications: (equipmentId: string) => void;
  getEditableEquipment: (equipmentId: string) => EditableEquipment;
  updateAutosaveSettings: (settings: Partial<{ enabled: boolean; intervalMinutes: number }>) => void;
  updateFeatureFlags: (flags: Partial<{ enableCustomTemplates: boolean; showBetaBadges: boolean; enableAdvancedCableRun: boolean }>) => void;
  resetFeatureFlags: () => void;
  startAutosaveTimer: () => void;
  stopAutosaveTimer: () => void;
  
  // Equipment customization actions
  saveEquipmentConfig: (equipmentId: string, config: any, options?: { updateExistingInstances?: boolean }) => void;
  getEquipmentConfig: (equipmentId: string) => any | null;
  clearEquipmentConfig: (equipmentId: string) => void;
  updateAllEquipmentInstances: (equipmentId: string, config: any) => void;
}

// Autosave timer reference
let autosaveTimer: NodeJS.Timeout | null = null;
export const AUTOSAVE_INTERVAL_MS = 2 * 60 * 1000;

// Storage key for equipment customizations
const EQUIPMENT_CUSTOMIZATIONS_KEY = 'netprobuilder-equipment-customizations';

// Load equipment customizations from localStorage
const loadEquipmentCustomizations = (): Record<string, { config: any }> => {
  try {
    const saved = localStorage.getItem(EQUIPMENT_CUSTOMIZATIONS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Migrate old format (layout only) to new format (full config)
      const migrated: Record<string, { config: any }> = {};
      Object.entries(parsed).forEach(([id, value]: [string, any]) => {
        if (value.layout && !value.config) {
          // Old format: migrate to new format
          migrated[id] = { config: { layout: value.layout } };
        } else {
          // New format: keep as is
          migrated[id] = value;
        }
      });
      return migrated;
    }
    return {};
  } catch {
    return {};
  }
};

// Save equipment customizations to localStorage
const saveEquipmentCustomizations = (customizations: Record<string, { config: any }>) => {
  try {
    localStorage.setItem(EQUIPMENT_CUSTOMIZATIONS_KEY, JSON.stringify(customizations));
  } catch (error) {
    console.error('Failed to save equipment customizations:', error);
  }
};

let workingCopyTimer: ReturnType<typeof setTimeout> | null = null;

const persistWorkingCopyNow = (state: { currentProject: Project | null; equipmentCustomizations: Record<string, { config: any }> }) => {
  if (!state.currentProject) return;
  writeProjectWorkingCopy(state.currentProject, state.equipmentCustomizations);
};

const scheduleWorkingCopyPersist = (getState: () => { currentProject: Project | null; equipmentCustomizations: Record<string, { config: any }> }) => {
  if (workingCopyTimer) clearTimeout(workingCopyTimer);
  workingCopyTimer = setTimeout(() => {
    workingCopyTimer = null;
    persistWorkingCopyNow(getState());
  }, 400);
};

const initialConfigForEquipmentType = (equipment: Equipment, savedConfig: any): any => {
  const { getCatalogDeviceModel } = require('../data/deviceModels');
  const catalogModel = getCatalogDeviceModel(equipment.id);
  if (savedConfig) {
    const cloned = JSON.parse(JSON.stringify(savedConfig));
    if (!cloned.deviceModel && catalogModel) {
      cloned.deviceModel = JSON.parse(JSON.stringify(catalogModel));
    }
    return cloned;
  }
  if (catalogModel?.layout) {
    return {
      layout: JSON.parse(JSON.stringify(catalogModel.layout)),
      numberingSchemeId: catalogModel.layout.numberingSchemeId,
      deviceModel: JSON.parse(JSON.stringify(catalogModel)),
    };
  }
  const portCount = equipment.specifications?.ports || 0;
  if (portCount > 0) {
    const { autoGenerateGridLayout } = require('../utils/autoGenerateLayout');
    return {
      layout: {
        simple: autoGenerateGridLayout(portCount),
      },
    };
  }
  return {};
};

const initialDesignerPrefs = resolveAccountUiPrefs(null);

const designerStateFromPrefs = (ui: AccountUiPrefs): Partial<ProjectState> => ({
  snapToGrid: ui.snapToGrid,
  snapToGridTarget: ui.snapToGridTarget,
  gridSize: ui.gridSize,
  iconGridCells: ui.iconGridCells,
  showGrid: ui.showGrid,
  showLegend: ui.showLegend,
  alwaysShowExpandedEquipment: ui.alwaysShowExpandedEquipment,
  portsDisplayMode: ui.portsDisplayMode,
  canvasBackground: ui.canvasBackground,
  badgeAnchor: ui.badgeAnchor,
  badgeShape: ui.badgeShape,
  panelVisualization: ui.panelVisualization,
  wallDragMode: ui.wallDragMode,
  showDesignInfoOverlay: ui.showDesignInfoOverlay,
  showMinimap: ui.showMinimap,
  showManufacturerInEquipmentList: ui.showManufacturerInEquipmentList,
  expandBadgesOnHover: ui.expandBadgesOnHover,
  colourizePorts: ui.colourizePorts,
  equipmentPanelAdvancedDefault: ui.equipmentPanelAdvancedDefault,
  equipmentPanelPreviewHidden: ui.equipmentPanelPreviewHidden,
  showAPRange: ui.showAPRange,
  currency: ui.currency,
  appTheme: ui.appTheme,
  appUIFont: ui.appUIFont,
  floorPlanUnits: ui.floorPlanUnits,
  autosaveSettings: {
    enabled: ui.autosaveEnabled,
    intervalMinutes: ui.autosaveIntervalMinutes,
  },
  featureFlags: {
    enableCustomTemplates: ui.enableCustomTemplates,
    showBetaBadges: ui.showBetaBadges,
    enableAdvancedCableRun: ui.enableAdvancedCableRun,
  },
  canvasCollabMode: ui.canvasCollabMode,
  cableRunRouting: ui.cableRunRouting,
});

const accountUiPrefsFromState = (state: ProjectState): AccountUiPrefs => ({
  snapToGrid: state.snapToGrid,
  snapToGridTarget: state.snapToGridTarget,
  gridSize: state.gridSize,
  iconGridCells: state.iconGridCells,
  showGrid: state.showGrid,
  showLegend: state.showLegend,
  alwaysShowExpandedEquipment: state.alwaysShowExpandedEquipment,
  portsDisplayMode: state.portsDisplayMode,
  canvasBackground: state.canvasBackground,
  badgeAnchor: state.badgeAnchor,
  badgeShape: state.badgeShape,
  panelVisualization: state.panelVisualization,
  wallDragMode: state.wallDragMode,
  showDesignInfoOverlay: state.showDesignInfoOverlay,
  showMinimap: state.showMinimap,
  showManufacturerInEquipmentList: state.showManufacturerInEquipmentList,
  expandBadgesOnHover: state.expandBadgesOnHover,
  colourizePorts: state.colourizePorts,
  equipmentPanelAdvancedDefault: state.equipmentPanelAdvancedDefault,
  equipmentPanelPreviewHidden: state.equipmentPanelPreviewHidden,
  showAPRange: state.showAPRange,
  autosaveEnabled: state.autosaveSettings.enabled,
  autosaveIntervalMinutes: state.autosaveSettings.intervalMinutes,
  currency: state.currency,
  appTheme: state.appTheme,
  appUIFont: state.appUIFont,
  showTowerLabels: resolveAccountUiPrefs(useAuthStore.getState().user?.id ?? null).showTowerLabels,
  floorPlanUnits: state.floorPlanUnits,
  enableCustomTemplates: state.featureFlags.enableCustomTemplates,
  showBetaBadges: state.featureFlags.showBetaBadges,
  enableAdvancedCableRun: state.featureFlags.enableAdvancedCableRun,
  canvasCollabMode: state.canvasCollabMode,
  cableRunRouting: state.cableRunRouting,
});

const persistDesignerPrefsFromState = (state: ProjectState) => {
  persistAccountUiPrefs(
    accountUiPrefsFromState(state),
    useAuthStore.getState().user?.id ?? null
  );
};

const persistCanvasCameraFromState = (state: ProjectState) => {
  const projectId = state.currentProject?.id;
  const designId = state.activeDesignId || state.currentDesignId;
  if (!projectId || !designId) return;
  writeCanvasCamera(useAuthStore.getState().user?.id ?? null, projectId, designId, {
    panX: state.canvasPanX,
    panY: state.canvasPanY,
    zoom: state.canvasZoom,
  });
};

const persistCanvasSessionFromState = (state: ProjectState) => {
  const projectId = state.currentProject?.id;
  const activeDesignId = state.activeDesignId || state.currentDesignId;
  if (!projectId || !activeDesignId) return;
  writeCanvasCameraSession(useAuthStore.getState().user?.id ?? null, projectId, {
    activeDesignId,
    openDesigns: state.openDesigns.length > 0 ? state.openDesigns : [activeDesignId],
  });
};

function previewHiddenFromDesign(design: { equipmentPanelPreviewHidden?: boolean } | undefined, fallback: boolean): boolean {
  if (design && typeof design.equipmentPanelPreviewHidden === 'boolean') {
    return design.equipmentPanelPreviewHidden;
  }
  return fallback;
}

const cameraStateForDesign = (
  project: Project,
  designId: string | null | undefined
): { canvasPanX: number; canvasPanY: number; canvasZoom: number } => {
  const settings = project.settings ?? {};
  const saved = designId
    ? readCanvasCamera(useAuthStore.getState().user?.id ?? null, project.id, designId)
    : null;
  return {
    canvasPanX: saved?.panX ?? settings.canvasPanX ?? 0,
    canvasPanY: saved?.panY ?? settings.canvasPanY ?? 0,
    canvasZoom: saved?.zoom ?? (typeof settings.canvasZoom === 'number' ? clampCanvasZoom(settings.canvasZoom) : 1),
  };
};

const applyProjectSettingsToState = (
  project: Project,
  set: (partial: Partial<ProjectState> | ((state: ProjectState) => Partial<ProjectState>)) => void,
  designId?: string | null
) => {
  const s = project.settings ?? {};
  const userId = useAuthStore.getState().user?.id ?? null;
  let ui = readSavedAccountUiPrefs(userId);
  if (!ui) {
    ui = mergeAccountUiPrefs(
      resolveAccountUiPrefs(userId),
      accountUiPrefsFromUnknown(s)
    );
    persistAccountUiPrefs(ui, userId);
  }

  set({
    ...designerStateFromPrefs(ui),
    ...cameraStateForDesign(project, designId),
    showCanvasToolbar: true,
  } as Partial<ProjectState>);
  useCanvasCollabStore.getState().setMode(ui.canvasCollabMode);
};

type DesignerSet = (
  partial: Partial<ProjectState> | ((state: ProjectState) => Partial<ProjectState>)
) => void;
type DesignerGet = () => ProjectState;

const patchDesignerSetting = (
  set: DesignerSet,
  get: DesignerGet,
  patch: Partial<ProjectState>
) => {
  const state = get();
  const proj = state.currentProject as Project & { settings?: Record<string, unknown> } | null;
  set({
    ...patch,
    currentProject: proj
      ? { ...proj, settings: { ...(proj.settings || {}), ...patch } }
      : state.currentProject,
  });
  persistDesignerPrefsFromState(get());
};

export const useProjectStore = create<ProjectState>((set, get) => ({
  currentProject: null,
  currentDesignId: null,
  openDesigns: [], // Array of design IDs open in tabs
  activeDesignId: null, // Currently active design tab
  openFloorplans: [], // Array of floorplan IDs open in tabs
  activeFloorplanId: null, // Currently active floorplan tab
  // Idle / place — no floor-plan tool lit until the user picks one
  canvasMode: 'place' as CanvasMode,
  selectedFloorPlanWallId: null,
  selectedFloorPlanWallIds: [],
  selectedFloorPlanRoomId: null,
  selectedFloorPlanOpeningId: null,
  selectedReferenceImageId: null,
  pendingReferenceImages: [],
  floorPlanHistoryPast: [],
  floorPlanHistoryFuture: [],
  pendingScaleWallId: null,
  cachedFloorplans: [],
  availableProjects: [],
  isProjectListOpen: false,
  lastSaved: null,
  isAutoSaving: false,
  autosaveSettings: {
    enabled: initialDesignerPrefs.autosaveEnabled,
    intervalMinutes: initialDesignerPrefs.autosaveIntervalMinutes,
  },
  badgeAnchor: initialDesignerPrefs.badgeAnchor,
  badgeShape: initialDesignerPrefs.badgeShape,
  featureFlags: {
    enableCustomTemplates: initialDesignerPrefs.enableCustomTemplates,
    showBetaBadges: initialDesignerPrefs.showBetaBadges,
    enableAdvancedCableRun: initialDesignerPrefs.enableAdvancedCableRun,
  },
  canvasCollabMode: initialDesignerPrefs.canvasCollabMode,
  selectedRack: null,
  selectedEquipment: null,
  selectedRackEquipment: null,
  selectedCable: null,
  pendingNewCableId: null,
  isCablePortEditorOpen: false,
  isRackConfigOpen: false,
  isEquipmentConfigOpen: false,
  isCableConfigOpen: false,
  isCableEditModalOpen: false,
  isSettingsConfigOpen: false,
  isSettingsPinned: false,
  isFloorplanDrawerOpen: false,
  isFloorplanSelectionOpen: false,
  isAddEquipmentOpen: false,
  isRackBuilderOpen: false,
  showAPRange: initialDesignerPrefs.showAPRange,
  draggedItem: null,
  isCableRunMode: false,
  cableRunRouting: initialDesignerPrefs.cableRunRouting,
  selectedSourceEquipment: null,
  hoveredCableId: null,
  hoveredEquipmentId: null,
  hoveredPortIdDuringCableDrag: null,
  // Canvas panning state
  canvasPanX: 0,
  canvasPanY: 0,
  canvasZoom: 1, // Default zoom level (1x = 100%)
  // Floorplan panning state
  floorplanPanX: 0,
  floorplanPanY: 0,
  draggedEquipmentPositions: {},
  isEquipmentDragActive: false,
  isRackPlacementMode: false,
  pendingRackPlacement: null,
  snapToGrid: initialDesignerPrefs.snapToGrid,
  snapToGridTarget: initialDesignerPrefs.snapToGridTarget,
  gridSize: initialDesignerPrefs.gridSize,
  iconGridCells: initialDesignerPrefs.iconGridCells,
  showGrid: initialDesignerPrefs.showGrid,
  showLegend: initialDesignerPrefs.showLegend,
  isExistingMode: false,
  alwaysShowExpandedEquipment: initialDesignerPrefs.alwaysShowExpandedEquipment,
  portsDisplayMode: initialDesignerPrefs.portsDisplayMode,
  canvasBackground: initialDesignerPrefs.canvasBackground,
  panelVisualization: initialDesignerPrefs.panelVisualization,
  wallDragMode: initialDesignerPrefs.wallDragMode,
  currency: initialDesignerPrefs.currency,
  appTheme: initialDesignerPrefs.appTheme,
  appUIFont: initialDesignerPrefs.appUIFont,
  floorPlanUnits: initialDesignerPrefs.floorPlanUnits,
  // Canvas toolbar is now the default, permanent layout
  showCanvasToolbar: true,
  showDesignInfoOverlay: initialDesignerPrefs.showDesignInfoOverlay,
  showMinimap: initialDesignerPrefs.showMinimap,
  showManufacturerInEquipmentList: initialDesignerPrefs.showManufacturerInEquipmentList,
  expandBadgesOnHover: initialDesignerPrefs.expandBadgesOnHover,
  colourizePorts: initialDesignerPrefs.colourizePorts,
  equipmentPanelAdvancedDefault: initialDesignerPrefs.equipmentPanelAdvancedDefault,
  equipmentPanelPreviewHidden: initialDesignerPrefs.equipmentPanelPreviewHidden,
  
  // Port system state
  selectedEquipmentForPortView: null,
  isPortVisualizationOpen: false,
  selectedEquipmentForEdit: null,
  isEquipmentEditorOpen: false,
  equipmentModifications: {},
  
  // Equipment customizations (permanent layouts per equipment type)
  equipmentCustomizations: loadEquipmentCustomizations(),

  // Adapter: return a logical Design representing the current project.
  // For now this simply wraps the single-design-per-project model.
  getCurrentDesign: () => {
    const state = get();
    const project = state.currentProject;
    if (!project) return null;

    // If the project already has a designs[] collection and a currentDesignId,
    // prefer that (future multi-design support). For now most projects won't
    // have this, so we fall back to the legacy single-design adapter.
    if (project.designs && project.designs.length > 0) {
      // Try activeDesignId first (from tab system), then currentDesignId
      const designIdToUse = state.activeDesignId || state.currentDesignId;
      if (designIdToUse) {
        const found = project.designs.find(d => d.id === designIdToUse);
        if (found) {
          const cables = uniqueCablesById(found.cables);
          return cables.length === (found.cables || []).length ? found : { ...found, cables };
        }
      }
      // Fallback to the first design in the collection
      const first = project.designs[0];
      const cables = uniqueCablesById(first.cables);
      return cables.length === (first.cables || []).length ? first : { ...first, cables };
    }

    return {
      id: project.id,
      name: project.name,
      createdAt: project.createdAt,
      updatedAt: project.updatedAt,
      racks: project.racks,
      equipment: project.equipment,
      cables: uniqueCablesById(project.cables),
      floorPlan: undefined,
    };
  },

  // Adapter: list designs for the current project. In the legacy model
  // this is always a single design derived from the project itself.
  getDesignsForCurrentProject: () => {
    const state = get();
    const project = state.currentProject;
    if (project?.designs && project.designs.length > 0) {
      return project.designs;
    }
    const design = state.getCurrentDesign();
    return design ? [design] : [];
  },

  setCurrentDesignId: (designId) => {
    const state = get();
    if (!state.currentProject || !state.currentProject.designs) return;
    const exists = state.currentProject.designs.some(d => d.id === designId);
    if (!exists) return;
    persistCanvasCameraFromState(state);
    const nextDesign = state.currentProject.designs.find((d) => d.id === designId);
    set({
      currentDesignId: designId,
      ...cameraStateForDesign(state.currentProject, designId),
      equipmentPanelPreviewHidden: previewHiddenFromDesign(nextDesign, state.equipmentPanelPreviewHidden),
    });
    persistCanvasSessionFromState(get());
  },

  createDesign: (name) => {
    const state = get();
    const project = state.currentProject;
    if (!project) return;

    const normalized = ensureProjectHasDesigns(project);
    const newDesign: Design = {
      id: generateId(),
      name: name || `Design ${normalized.designs ? normalized.designs.length + 1 : 1}`,
      createdAt: new Date(),
      updatedAt: new Date(),
      racks: [],
      equipment: [],
      cables: [],
      logicMode: 'plan',
    };

    const updatedProject: Project = {
      ...normalized,
      designs: [...(normalized.designs || []), newDesign],
      updatedAt: new Date(),
    };

    set({
      currentProject: updatedProject,
      currentDesignId: newDesign.id,
    });

    // Persist the updated project and refresh list
    saveProjectToStorage(updatedProject);
    state.refreshAvailableProjects();
    
    // Automatically open the new design in a tab
    state.openDesignTab(newDesign.id);
  },

  renameDesign: (designId, name) => {
    const state = get();
    const project = state.currentProject;
    if (!project || !project.designs) return;

    const trimmed = name.trim();
    if (!trimmed) return;

    const updatedDesigns = project.designs.map((d) =>
      d.id === designId ? { ...d, name: trimmed, updatedAt: new Date() } : d
    );

    const updatedProject: Project = {
      ...project,
      designs: updatedDesigns,
      updatedAt: new Date(),
    };

    set({ currentProject: updatedProject });
    saveProjectToStorage(updatedProject);
    state.refreshAvailableProjects();
  },

  deleteDesign: (designId) => {
    const state = get();
    const project = state.currentProject;
    if (!project || !project.designs) return;

    // Prevent deleting the last remaining design for safety
    if (project.designs.length <= 1) {
      console.warn('Cannot delete the last design in a project.');
      return;
    }

    const updatedDesigns = project.designs.filter((d) => d.id !== designId);
    if (updatedDesigns.length === project.designs.length) return;

    let newCurrentDesignId = state.currentDesignId;
    if (!newCurrentDesignId || newCurrentDesignId === designId) {
      newCurrentDesignId = updatedDesigns[0].id;
    }

    const updatedProject: Project = {
      ...project,
      designs: updatedDesigns,
      updatedAt: new Date(),
    };

    set({
      currentProject: updatedProject,
      currentDesignId: newCurrentDesignId,
    });
    saveProjectToStorage(updatedProject);
    state.refreshAvailableProjects();
    
    // Also close the tab if it was open
    state.closeDesignTab(designId);
  },

  // Tab management methods
  openDesignTab: (designId) => {
    const state = get();
    const project = state.currentProject;
    if (!project) return;
    
    // Verify design exists in current project
    const designs = state.getDesignsForCurrentProject();
    const designExists = designs.some(d => d.id === designId);
    if (!designExists) return;

    persistCanvasCameraFromState(state);
    const camera = cameraStateForDesign(project, designId);
    
    // Add to openDesigns if not already there
    if (!state.openDesigns.includes(designId)) {
      set({ 
        openDesigns: [...state.openDesigns, designId],
        activeDesignId: designId,
        currentDesignId: designId,
        ...camera,
      });
    } else {
      set({ 
        activeDesignId: designId,
        currentDesignId: designId,
        ...camera,
      });
    }
    persistCanvasSessionFromState(get());
  },

  closeDesignTab: (designId) => {
    const state = get();
    persistCanvasCameraFromState(state);
    const updatedOpenDesigns = state.openDesigns.filter(id => id !== designId);
    
    // If closing the active tab, switch to another one
    let newActiveDesignId = state.activeDesignId;
    if (state.activeDesignId === designId) {
      if (updatedOpenDesigns.length > 0) {
        newActiveDesignId = updatedOpenDesigns[0];
      } else {
        newActiveDesignId = null;
      }
    }

    const camera = state.currentProject && newActiveDesignId
      ? cameraStateForDesign(state.currentProject, newActiveDesignId)
      : { canvasPanX: 0, canvasPanY: 0, canvasZoom: 1 };
    
    set({ 
      openDesigns: updatedOpenDesigns,
      activeDesignId: newActiveDesignId,
      currentDesignId: newActiveDesignId,
      ...camera,
    });
    persistCanvasSessionFromState(get());
  },

  setActiveDesignTab: (designId) => {
    const state = get();
    // Only switch if the design is already open
    if (state.openDesigns.includes(designId)) {
      persistCanvasCameraFromState(state);
      const nextDesign = state.currentProject?.designs?.find((d) => d.id === designId);
      set({ 
        activeDesignId: designId,
        currentDesignId: designId,
        ...(state.currentProject ? cameraStateForDesign(state.currentProject, designId) : {}),
        equipmentPanelPreviewHidden: previewHiddenFromDesign(nextDesign, state.equipmentPanelPreviewHidden),
      });
      persistCanvasSessionFromState(get());
    }
  },

  getOpenDesigns: () => {
    const state = get();
    const designs = state.getDesignsForCurrentProject();
    return designs.filter(d => state.openDesigns.includes(d.id));
  },

  // Floorplan tab management methods
  openFloorplanTab: (floorplanId) => {
    const state = get();
    
    // Verify floorplan exists (check global floorplans, not project-specific)
    const allFloorplans = state.getFloorplans();
    const floorplanExists = allFloorplans.some(fp => fp.id === floorplanId);
    if (!floorplanExists) {
      // If floorplan doesn't exist, try loading it directly
      const loaded = state.loadFloorplan(floorplanId);
      if (!loaded) {
        console.warn('Floorplan not found:', floorplanId);
        return;
      }
    }
    
    // Add to openFloorplans if not already there
    if (!state.openFloorplans.includes(floorplanId)) {
      set({ 
        openFloorplans: [...state.openFloorplans, floorplanId],
        activeFloorplanId: floorplanId
      });
    } else {
      // Just switch to it if already open
      set({ 
        activeFloorplanId: floorplanId
      });
    }
  },

  closeFloorplanTab: (floorplanId) => {
    const state = get();
    const updatedOpenFloorplans = state.openFloorplans.filter(id => id !== floorplanId);
    
    // If closing the active tab, switch to another one
    let newActiveFloorplanId = state.activeFloorplanId;
    if (state.activeFloorplanId === floorplanId) {
      if (updatedOpenFloorplans.length > 0) {
        newActiveFloorplanId = updatedOpenFloorplans[0];
      } else {
        newActiveFloorplanId = null;
      }
    }
    
    set({ 
      openFloorplans: updatedOpenFloorplans,
      activeFloorplanId: newActiveFloorplanId
    });
  },

  setActiveFloorplanTab: (floorplanId) => {
    const state = get();
    // Only switch if the floorplan is already open
    if (state.openFloorplans.includes(floorplanId)) {
      set({ 
        activeFloorplanId: floorplanId
      });
    }
  },

  getOpenFloorplans: () => {
    const state = get();
    const floorplans = state.getFloorplansForCurrentProject();
    return floorplans.filter(fp => state.openFloorplans.includes(fp.id));
  },

  getFloorplansForCurrentProject: () => {
    const state = get();
    const project = state.currentProject;
    if (!project) return [];

    // If the project already has a floorplans collection, prefer that.
    const floorplans = (project as any).floorplans as FloorplanData[] | undefined;
    if (floorplans && Array.isArray(floorplans)) {
      return floorplans;
    }

    // Fallback: get all floorplans from localStorage (they're stored globally, not per-project)
    return state.getFloorplans();
  },

  getSitesForCurrentProject: () => {
    const project = get().currentProject as any;
    if (!project) return [];
    const sites = project.sites;
    return Array.isArray(sites) ? sites : [];
  },

  createProject: (name, customerName, address) => {
    const userId = useAuthStore.getState().user?.id;
    const designerPrefs = readSavedAccountUiPrefs(userId) ?? accountUiPrefsFromState(get());
    const designerSettings = settingsForNewCollection(designerPrefs);
    if (userId) {
      void (async () => {
        try {
          const customer = { name: customerName, address };
          const collection = await createCollectionRecord(userId, name, customer, designerSettings);
          const now = new Date();
          await insertDesignRecord(
            collection.id,
            {
              id: collection.id,
              name,
              createdAt: now,
              updatedAt: now,
              racks: [],
              equipment: [],
              cables: [],
            },
            0
          );
          const project = await loadCollectionAsProject(collection.id);
          if (project) {
            get().hydrateProject(project);
            setLastCollectionId(collection.id);
            setCollectionPartialState({ currentCollectionId: collection.id });
            await get().refreshAvailableProjectsFromCloud(userId);
          }
        } catch (err) {
          console.error('Failed to create collection:', err);
        }
      })();
      return;
    }

    const baseProject: Project = {
      id: generateId(),
      name,
      customer: {
        name: customerName,
        address
      },
      racks: [],
      equipment: [],
      cables: [],
      settings: designerSettings,
      createdAt: new Date(),
      updatedAt: new Date()
    } as any;

    const projectWithDesigns = ensureProjectHasDesigns(baseProject);
    const firstDesignId = projectWithDesigns.designs ? projectWithDesigns.designs[0].id : projectWithDesigns.id;

    set({
      currentProject: projectWithDesigns,
      currentDesignId: firstDesignId,
    });
    applyProjectSettingsToState(projectWithDesigns, set, firstDesignId);
    persistDesignerPrefsFromState(get());
    // Auto-save new project
    saveProjectToStorage(projectWithDesigns);
    get().refreshAvailableProjects();
    // Start autosave timer
    get().startAutosaveTimer();
  },
  setBadgeAnchor: (anchor) => {
    const valid = ['top-left','top-right','center-top','left','right','bottom-left','bottom-right','center-bottom'] as const;
    const safe = (valid as readonly string[]).includes(anchor) ? anchor : 'top-left';
    patchDesignerSetting(set, get, { badgeAnchor: safe as ProjectState['badgeAnchor'] });
  },
  setBadgeShape: (shape) => {
    patchDesignerSetting(set, get, { badgeShape: shape === 'circle' ? 'circle' : 'square' });
  },

  persistWorkingCopy: (immediate = true) => {
    if (immediate) {
      if (workingCopyTimer) {
        clearTimeout(workingCopyTimer);
        workingCopyTimer = null;
      }
      persistWorkingCopyNow(get());
      return;
    }
    scheduleWorkingCopyPersist(get);
  },

  saveProject: () => {
    const state = get();
    if (state.currentProject) {
      set({ isAutoSaving: true });
      // Embed current working settings into the project before saving (per-design persistence)
      const mergedSettings = {
        ...(state.currentProject as any).settings,
        snapToGrid: state.snapToGrid,
        snapToGridTarget: state.snapToGridTarget,
        gridSize: state.gridSize,
        iconGridCells: state.iconGridCells,
        showGrid: state.showGrid,
        showLegend: state.showLegend,
        alwaysShowExpandedEquipment: state.alwaysShowExpandedEquipment,
        portsDisplayMode: state.portsDisplayMode,
        canvasBackground: state.canvasBackground,
        badgeAnchor: state.badgeAnchor,
        badgeShape: state.badgeShape,
        panelVisualization: state.panelVisualization,
        wallDragMode: state.wallDragMode,
        canvasPanX: state.canvasPanX,
        canvasPanY: state.canvasPanY,
        canvasZoom: state.canvasZoom,
        showCanvasToolbar: state.showCanvasToolbar,
        showDesignInfoOverlay: state.showDesignInfoOverlay,
        showMinimap: state.showMinimap,
        showManufacturerInEquipmentList: state.showManufacturerInEquipmentList,
        expandBadgesOnHover: state.expandBadgesOnHover
      };
      const activeId = state.activeDesignId || state.currentDesignId;
      const updatedProject = {
        ...state.currentProject,
        settings: mergedSettings,
        updatedAt: new Date(),
        designs: (state.currentProject.designs || []).map((d) =>
          d.id === activeId
            ? { ...d, equipmentPanelPreviewHidden: state.equipmentPanelPreviewHidden }
            : d
        ),
      } as Project;

      set({ currentProject: updatedProject });
      persistWorkingCopyNow({
        currentProject: updatedProject,
        equipmentCustomizations: get().equipmentCustomizations,
      });

      const userId = useAuthStore.getState().user?.id;
      if (userId) {
        void saveCollectionProject(updatedProject, userId)
          .then(() => {
            set({
              lastSaved: new Date(),
              isAutoSaving: false,
            });
            void get().refreshAvailableProjectsFromCloud(userId);
          })
          .catch((err) => {
            console.error('Failed to save collection:', err);
            set({ isAutoSaving: false });
          });
      } else {
        saveProjectToStorage(updatedProject);
        set({
          lastSaved: new Date(),
          isAutoSaving: false,
        });
        get().refreshAvailableProjects();
      }
    }
  },

  saveProjectForCollab: async (generation) => {
    const state = get();
    if (!state.currentProject || state.canvasCollabMode === 'default') return;

    set({ isAutoSaving: true });
    const mergedSettings = {
      ...(state.currentProject as any).settings,
      snapToGrid: state.snapToGrid,
      snapToGridTarget: state.snapToGridTarget,
      gridSize: state.gridSize,
      iconGridCells: state.iconGridCells,
      showGrid: state.showGrid,
      showLegend: state.showLegend,
      alwaysShowExpandedEquipment: state.alwaysShowExpandedEquipment,
      portsDisplayMode: state.portsDisplayMode,
      canvasBackground: state.canvasBackground,
      badgeAnchor: state.badgeAnchor,
      badgeShape: state.badgeShape,
      panelVisualization: state.panelVisualization,
      wallDragMode: state.wallDragMode,
      canvasPanX: state.canvasPanX,
      canvasPanY: state.canvasPanY,
      canvasZoom: state.canvasZoom,
      showCanvasToolbar: state.showCanvasToolbar,
      showDesignInfoOverlay: state.showDesignInfoOverlay,
      showMinimap: state.showMinimap,
      showManufacturerInEquipmentList: state.showManufacturerInEquipmentList,
      expandBadgesOnHover: state.expandBadgesOnHover,
    };
    const snapshot = {
      ...state.currentProject,
      settings: mergedSettings,
      updatedAt: new Date(),
    } as Project;

    const userId = useAuthStore.getState().user?.id;
    if (!userId) {
      set({ isAutoSaving: false });
      return;
    }

    const writeId = beginCollabWrite();
    const currentIds = (snapshot.designs ?? []).map((d) => d.id);
    const removed = removedDesignIds(currentIds);

    try {
      await saveCollectionProject(snapshot, userId, {
        clientWriteId: writeId,
        removedDesignIds: removed,
      });
      rememberDesignIds(currentIds);
      if (getCanvasSaveGeneration() !== generation) {
        set({ isAutoSaving: false });
        return;
      }
      set({ lastSaved: new Date(), isAutoSaving: false });
    } catch (err) {
      console.error('Failed to save collection (collab):', err);
      set({ isAutoSaving: false });
    }
  },

  applyRemoteDesign: (event, row) => {
    const state = get();
    if (!state.currentProject || state.canvasCollabMode === 'default') return;
    if (state.isEquipmentDragActive) return;

    const designId = (row.legacy_design_id as string | undefined) ?? (row.id as string | undefined);
    if (!designId) return;

    if (event === 'DELETE') {
      const project = ensureProjectHasDesigns(state.currentProject);
      set({
        currentProject: {
          ...project,
          designs: (project.designs ?? []).filter((d) => d.id !== designId),
        },
      });
      return;
    }

    const remote: Design = {
      id: designId,
      name: (row.name as string) ?? 'Design',
      createdAt: row.created_at ? new Date(row.created_at as string) : new Date(),
      updatedAt: row.updated_at ? new Date(row.updated_at as string) : new Date(),
      racks: (row.racks as Design['racks']) ?? [],
      equipment: (row.equipment as Design['equipment']) ?? [],
      cables: (row.cables as Design['cables']) ?? [],
      boxes: (row.boxes as Design['boxes']) ?? undefined,
      labels: (row.labels as Design['labels']) ?? undefined,
      floorPlan: (row.floor_plan as Design['floorPlan']) ?? undefined,
      ...designSettingsFromRow(row.settings),
    };

    const project = ensureProjectHasDesigns(state.currentProject);
    const existing = (project.designs ?? []).find((d) => d.id === designId);
    const dirty =
      getLastLocalCanvasMutationAt() > 0 &&
      Date.now() - getLastLocalCanvasMutationAt() < 8000;

    let nextDesign = remote;
    if (dirty && existing) {
      nextDesign = {
        ...remote,
        racks: mergeById(existing.racks ?? [], remote.racks ?? []),
        equipment: mergeById(existing.equipment ?? [], remote.equipment ?? []),
        cables: mergeById(existing.cables ?? [], remote.cables ?? []),
        boxes: mergeById(existing.boxes ?? [], remote.boxes ?? []),
        labels: mergeById(existing.labels ?? [], remote.labels ?? []),
        floorPlan: existing.floorPlan ?? remote.floorPlan,
        equipmentPanelPreviewHidden: existing.equipmentPanelPreviewHidden,
      };
    }

    const designs = existing
      ? (project.designs ?? []).map((d) => (d.id === designId ? nextDesign : d))
      : [...(project.designs ?? []), nextDesign];

    set({
      currentProject: {
        ...project,
        designs,
        updatedAt: nextDesign.updatedAt,
      },
    });
  },

  setCanvasCollabMode: (mode) => {
    set({ canvasCollabMode: mode });
    useCanvasCollabStore.getState().setMode(mode);
    persistDesignerPrefsFromState(get());
    restartCollectionRealtime();
  },

  hydrateProject: (project) => {
    persistCanvasCameraFromState(get());
    persistCanvasSessionFromState(get());

    let withDesigns = ensureProjectHasDesigns(project);
    const cached = readProjectWorkingCopy(withDesigns.id);
    const restoredFromWorkingCopy = !!(cached && workingCopyIsNewer(cached, withDesigns));
    if (restoredFromWorkingCopy && cached) {
      withDesigns = ensureProjectHasDesigns(cached.project);
      if (cached.customizations && Object.keys(cached.customizations).length > 0) {
        const mergedCustomizations = {
          ...get().equipmentCustomizations,
          ...cached.customizations,
        };
        set({ equipmentCustomizations: mergedCustomizations });
        saveEquipmentCustomizations(mergedCustomizations);
      }
    }
    const firstDesignId = withDesigns.designs ? withDesigns.designs[0].id : withDesigns.id;
    const designIds = new Set((withDesigns.designs ?? []).map((d) => d.id));
    if (designIds.size === 0) designIds.add(firstDesignId);

    const session = readCanvasCameraSession(useAuthStore.getState().user?.id ?? null, withDesigns.id);
    const openDesigns = (session?.openDesigns ?? []).filter((id) => designIds.has(id));
    const restoredOpen = openDesigns.length > 0 ? openDesigns : [firstDesignId];
    const activeDesignId =
      session?.activeDesignId && restoredOpen.includes(session.activeDesignId)
        ? session.activeDesignId
        : restoredOpen[0];

    set({
      currentProject: withDesigns,
      currentDesignId: activeDesignId,
      openDesigns: restoredOpen,
      activeDesignId,
      lastSaved: withDesigns.updatedAt,
      isRackConfigOpen: false,
      isEquipmentConfigOpen: false,
      isCableConfigOpen: false,
      selectedRack: null,
      selectedEquipment: null,
      selectedCable: null,
      pendingNewCableId: null,
    });
    applyProjectSettingsToState(withDesigns, set, activeDesignId);
    const hydratedDesign = (withDesigns.designs ?? []).find((d) => d.id === activeDesignId);
    if (hydratedDesign && typeof hydratedDesign.equipmentPanelPreviewHidden === 'boolean') {
      set({ equipmentPanelPreviewHidden: hydratedDesign.equipmentPanelPreviewHidden });
    }
    rememberDesignIds((withDesigns.designs ?? []).map((d) => d.id));
    useCanvasCollabStore.getState().setMode(get().canvasCollabMode);
    get().startAutosaveTimer();
    if (restoredFromWorkingCopy) {
      const restoredId = withDesigns.id;
      queueMicrotask(() => {
        if (get().currentProject?.id === restoredId) {
          get().saveProject();
        }
      });
    }
  },

  applyAccountUiPrefs: (prefs) => {
    const prev = get();
    const next = designerStateFromPrefs(prefs) as Partial<ProjectState>;
    const unchanged =
      prev.equipmentPanelPreviewHidden === next.equipmentPanelPreviewHidden &&
      prev.equipmentPanelAdvancedDefault === next.equipmentPanelAdvancedDefault &&
      prev.colourizePorts === next.colourizePorts &&
      prev.panelVisualization === next.panelVisualization &&
      prev.portsDisplayMode === next.portsDisplayMode &&
      prev.badgeAnchor === next.badgeAnchor &&
      prev.badgeShape === next.badgeShape &&
      prev.appTheme === next.appTheme &&
      prev.appUIFont === next.appUIFont &&
      prev.canvasCollabMode === next.canvasCollabMode &&
      prev.cableRunRouting === next.cableRunRouting &&
      prev.snapToGrid === next.snapToGrid &&
      prev.gridSize === next.gridSize &&
      prev.showGrid === next.showGrid &&
      prev.canvasBackground === next.canvasBackground &&
      prev.alwaysShowExpandedEquipment === next.alwaysShowExpandedEquipment &&
      prev.expandBadgesOnHover === next.expandBadgesOnHover &&
      prev.showAPRange === next.showAPRange &&
      prev.autosaveSettings.enabled === prefs.autosaveEnabled &&
      prev.autosaveSettings.intervalMinutes === prefs.autosaveIntervalMinutes;

    if (!unchanged) {
      set(next);
    }
    applyAppUIFont(prefs.appUIFont);
    useCanvasCollabStore.getState().setMode(prefs.canvasCollabMode);
    if (prev.canvasCollabMode !== prefs.canvasCollabMode) {
      restartCollectionRealtime();
    }
    void import('./siteStore').then(({ useSiteStore }) => {
      useSiteStore.setState({ showTowerLabels: prefs.showTowerLabels });
    });
    get().stopAutosaveTimer();
    if (prefs.autosaveEnabled && get().currentProject) {
      get().startAutosaveTimer();
    }
  },

  loadProject: (projectId) => {
    const userId = useAuthStore.getState().user?.id;
    if (userId) {
      void switchCollectionById(projectId).catch((err) => {
        console.error('[loadProject] switchCollection failed:', err);
      });
      return;
    }

    const loaded = loadProjectFromStorage(projectId);
    if (loaded) {
      get().hydrateProject(loaded);
    }
  },

  loadLastProject: () => {
    if (useAuthStore.getState().user?.id) return;
    const lastProjectId = getLastProjectId();
    if (lastProjectId) {
      get().loadProject(lastProjectId);
    }
  },

  deleteProject: (projectId) => {
    clearProjectWorkingCopy(projectId);
    const userId = useAuthStore.getState().user?.id;
    if (userId) {
      void deleteCollectionRecord(projectId, userId).then(async () => {
        await get().refreshAvailableProjectsFromCloud(userId);
        // If the deleted collection was active, switch to the next available one
        const remaining = get().availableProjects;
        const wasActive = get().currentProject?.id === projectId;
        if (wasActive) {
          if (remaining.length > 0) {
            get().loadProject(remaining[0].id);
          } else {
            set({
              currentProject: null,
              lastSaved: null,
              isRackConfigOpen: false,
              isEquipmentConfigOpen: false,
              isCableConfigOpen: false,
              selectedRack: null,
              selectedEquipment: null,
              selectedCable: null,
              cachedFloorplans: [],
            });
            setCollectionPartialState({ currentCollectionId: null });
          }
        }
      });
      return;
    }

    deleteProjectFromStorage(projectId);
    get().refreshAvailableProjects();
    const state = get();
    if (state.currentProject?.id === projectId) {
      set({
        currentProject: null,
        lastSaved: null,
        isRackConfigOpen: false,
        isEquipmentConfigOpen: false,
        isCableConfigOpen: false,
        selectedRack: null,
        selectedEquipment: null,
        selectedCable: null,
      });
    }
  },

  renameProject: (projectId, name) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const userId = useAuthStore.getState().user?.id;
    if (userId) {
      void renameCollectionRecord(projectId, userId, trimmedName).then(() => {
        const state = get();
        if (state.currentProject?.id === projectId) {
          set({
            currentProject: { ...state.currentProject, name: trimmedName, updatedAt: new Date() },
            lastSaved: new Date(),
          });
        }
        void get().refreshAvailableProjectsFromCloud(userId);
      });
      return;
    }

    const loadedProject = loadProjectFromStorage(projectId);
    if (!loadedProject) return;

    const updatedProject = ensureProjectHasDesigns({
      ...loadedProject,
      name: trimmedName,
      updatedAt: new Date(),
    });

    saveProjectToStorage(updatedProject);

    const state = get();
    if (state.currentProject?.id === projectId) {
      set({
        currentProject: updatedProject,
        lastSaved: new Date(),
      });
    }

    get().refreshAvailableProjects();
  },

  refreshAvailableProjectsFromCloud: async (userId) => {
    try {
      const collections = await fetchCollectionsForUser(userId);
      const designCounts = await fetchDesignCountsByCollection(collections.map((c) => c.id));
      const projects: Project[] = collections.map((c) => ({
        ...collectionToProject(c, []),
        designCount: designCounts[c.id] ?? 0,
      }));
      set({ availableProjects: projects });
    } catch (err) {
      console.error('Failed to refresh collections:', err);
    }
  },

  refreshAvailableProjects: () => {
    const userId = useAuthStore.getState().user?.id;
    if (userId) {
      void get().refreshAvailableProjectsFromCloud(userId);
      return;
    }
    const projects = getAllProjects();
    set({
      availableProjects: projects.map((p) => ({
        ...p,
        designCount: p.designs?.length ?? 0,
      })),
    });
  },

  setProjectListOpen: (open) => set({ isProjectListOpen: open }),

  addRack: (size, position, equipment = [], name, siteId = null) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const rack: Rack = {
      id: generateId(),
      name: name || `${size}U Rack`,
      size,
      position,
      equipment,
      siteId: siteId || null,
    };

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, racks: [...d.racks, rack], updatedAt: new Date() } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      }
    });
    requestCanvasCommit();
    
  },

  removeRack: (rackId) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    // Remove all cables connected to this rack within the current design
    const updatedCables = (design.cables || []).filter(cable => 
      !cable.from.startsWith(rackId) && !cable.to.startsWith(rackId)
    );

    // Remove the rack within the current design
    const updatedRacks = (design.racks || []).filter(rack => rack.id !== rackId);

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, racks: updatedRacks, cables: updatedCables, updatedAt: new Date() } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      },
      // Clear selected rack if it was deleted
      selectedRack: state.selectedRack?.id === rackId ? null : state.selectedRack,
      isRackConfigOpen: state.selectedRack?.id === rackId ? false : state.isRackConfigOpen,
      selectedRackEquipment: state.selectedRackEquipment?.rackId === rackId ? null : state.selectedRackEquipment,
      isEquipmentConfigOpen: state.selectedRackEquipment?.rackId === rackId ? false : state.isEquipmentConfigOpen
    });
    requestCanvasCommit();

  },

  updateRack: (rackId, updates) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedRacks = (design.racks || []).map(rack =>
      rack.id === rackId ? { ...rack, ...updates } : rack
    );

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, racks: updatedRacks, updatedAt: new Date() } : d
    );

    const updatedSelected =
      state.selectedRack?.id === rackId
        ? { ...state.selectedRack, ...updates }
        : state.selectedRack;

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      },
      selectedRack: updatedSelected,
    });

  },

  updateRackPosition: (rackId, position) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedRacks = (design.racks || []).map(rack =>
      rack.id === rackId ? { ...rack, position } : rack
    );

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, racks: updatedRacks } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns
        // Don't update updatedAt during drag for better performance
      }
    });
    requestCanvasCommit();
  },

  addEquipmentToRack: (rackId, equipment, startUnit) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    // Check for saved config customization - this "marries" the config to the equipment
    const initialConfig = initialConfigForEquipmentType(equipment, get().getEquipmentConfig(equipment.id));

    const updatedRacks = (design.racks || []).map(rack => {
      if (rack.id === rackId) {
        return {
          ...rack,
          equipment: [...rack.equipment, { equipment, startUnit, config: initialConfig }]
        };
      }
      return rack;
    });

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, racks: updatedRacks, updatedAt: new Date() } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      }
    });
  },

  moveRackEquipment: (rackId, fromIndex, toStartUnit) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedRacks = (design.racks || []).map(rack => {
      if (rack.id === rackId) {
        const newEquipment = [...rack.equipment];
        const [movedEquipment] = newEquipment.splice(fromIndex, 1);
        
        // Update the start unit
        movedEquipment.startUnit = toStartUnit;
        
        // Sort equipment by start unit and insert back
        newEquipment.push(movedEquipment);
        newEquipment.sort((a, b) => a.startUnit - b.startUnit);
        
        return {
          ...rack,
          equipment: newEquipment
        };
      }
      return rack;
    });

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, racks: updatedRacks, updatedAt: new Date() } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      }
    });
  },

  updateRackEquipmentConfig: (rackId, equipmentIndex, config) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedRacks = (design.racks || []).map(rack => {
      if (rack.id === rackId) {
        const updatedEquipment = rack.equipment.map((eq, index) => {
          if (index === equipmentIndex) {
            return { ...eq, config: { ...eq.config, ...config } };
          }
          return eq;
        });
        
        return {
          ...rack,
          equipment: updatedEquipment
        };
      }
      return rack;
    });

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, racks: updatedRacks, updatedAt: new Date() } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      }
    });
    
    // Update selected rack equipment if it's the one being updated
    const selectedRackEq = state.selectedRackEquipment;
    if (selectedRackEq && selectedRackEq.rackId === rackId && selectedRackEq.index === equipmentIndex) {
      const updatedRack = updatedRacks.find(r => r.id === rackId);
      if (updatedRack) {
        set({
          selectedRackEquipment: {
            ...selectedRackEq,
            equipment: { ...selectedRackEq.equipment, config: { ...selectedRackEq.equipment.config, ...config } }
          }
        });
      }
    }
  },

  addEquipmentToCanvas: (equipment, position) => {
    try {
      const state = get();
      if (!state.currentProject) {
        console.error('No current project when trying to add equipment');
        return;
      }

      if (!equipment) {
        console.error('No equipment provided to addEquipmentToCanvas');
        return;
      }

      if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
        console.error('Invalid position provided to addEquipmentToCanvas:', position);
        return;
      }

      // Generate unique name with auto-numbering
      const project = ensureProjectHasDesigns(state.currentProject);
      const design = state.getCurrentDesign();
      if (!design) {
        console.error('No current design when trying to add equipment');
        return;
      }

      const uniqueName = generateUniqueEquipmentName(equipment.name, design.equipment);
      
      // Check for saved config customization - this "marries" the config to the equipment
      // equipment.id is the base equipment ID from the database (e.g., 'ubnt-us-8')
      const initialConfig = initialConfigForEquipmentType(equipment, get().getEquipmentConfig(equipment.id));
      
      const placedEquipment: PlacedEquipment = {
        id: generateId(),
        equipment: {
          ...equipment,
          name: uniqueName
        },
        position,
        config: initialConfig
      };

      const updatedDesigns = (project.designs || []).map(d =>
        d.id === design.id
          ? {
              ...d,
              equipment: [...d.equipment, placedEquipment],
              updatedAt: new Date()
            }
          : d
      );

      set({
        currentProject: {
          ...project,
          designs: updatedDesigns,
          updatedAt: new Date()
        }
      });
      requestCanvasCommit();
    } catch (error) {
      console.error('Error adding equipment to canvas:', error);
    }
  },

  duplicatePlacedEquipment: (equipmentId) => {
    const state = get();
    if (!state.currentProject) return;
    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;
    const source = (design.equipment || []).find((eq) => eq.id === equipmentId);
    if (!source) return;
    const uniqueName = generateUniqueEquipmentName(source.equipment.name, design.equipment);
    const cloned: PlacedEquipment = {
      ...source,
      id: generateId(),
      equipment: { ...source.equipment, name: uniqueName },
      position: { x: source.position.x + 48, y: source.position.y + 48 },
      config: source.config ? JSON.parse(JSON.stringify(source.config)) : {},
    };
    const updatedDesigns = (project.designs || []).map((d) =>
      d.id === design.id
        ? { ...d, equipment: [...d.equipment, cloned], updatedAt: new Date() }
        : d
    );
    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date(),
      },
      selectedEquipment: cloned,
    });
    requestCanvasCommit();
  },

  updateEquipmentConfig: (equipmentId, config, options) => {
    const state = get();
    if (!state.currentProject) {
      console.error('Cannot update equipment config: no current project');
      return;
    }

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) {
      console.error('Cannot update equipment config: no current design');
      return;
    }

    const updatedEquipment = (design.equipment || []).map(eq => {
      if (eq.id === equipmentId) {
        const newConfig = { ...(eq.config || {}), ...config };

        // Layout/config is instance-scoped unless persistToLibrary is requested.
        // Default stays true so existing callers (labels, panel prefs) keep previous behavior.
        if (options?.persistToLibrary !== false && eq.equipment.id) {
          const existingConfig = get().getEquipmentConfig(eq.equipment.id);
          const mergedConfig = existingConfig ? { ...existingConfig, ...config } : config;
          get().saveEquipmentConfig(eq.equipment.id, mergedConfig, { updateExistingInstances: false });
        }

        return { ...eq, config: newConfig };
      }
      return eq;
    });

    // Find the updated equipment to update selectedEquipment as well
    const updatedSelectedEquipment = updatedEquipment.find(eq => eq.id === equipmentId);

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, equipment: updatedEquipment, updatedAt: new Date() } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      },
      // Update selectedEquipment if it's the one being modified
      selectedEquipment: state.selectedEquipment?.id === equipmentId ? updatedSelectedEquipment || state.selectedEquipment : state.selectedEquipment
    });
    persistWorkingCopyNow(get());
    requestCanvasCommit();
  },

  patchCurrentDesign: (patch) => {
    const state = get();
    if (!state.currentProject) return;
    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;
    const updatedDesigns = (project.designs || []).map((d) =>
      d.id === design.id ? { ...d, ...patch, updatedAt: new Date() } : d
    );
    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date(),
      },
    });
    requestCanvasCommit();
  },

  updateProjectCustomer: (customer) => {
    const state = get();
    if (!state.currentProject) return;

    set({
      currentProject: {
        ...state.currentProject,
        customer: {
          ...state.currentProject.customer,
          ...customer
        },
        updatedAt: new Date()
      }
    });
    
  },

  removeEquipment: (equipmentId) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    // Remove cables connected to this equipment's ports
    const removedCableIds: string[] = [];
    const updatedCables = (design.cables || []).filter((cable) => {
      return !(
        (cable.from.startsWith(`${equipmentId}:port:`) ||
        cable.to.startsWith(`${equipmentId}:port:`)) &&
        removedCableIds.push(cable.id)
      );
    });

    // Remove the equipment from canvas
    const updatedEquipment = (design.equipment || []).filter(eq => eq.id !== equipmentId);

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id
        ? { ...d, equipment: updatedEquipment, cables: updatedCables, updatedAt: new Date() }
        : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      },
      selectedEquipment: state.selectedEquipment?.id === equipmentId ? null : state.selectedEquipment,
      isEquipmentConfigOpen: state.selectedEquipment?.id === equipmentId ? false : state.isEquipmentConfigOpen,
      // Clear cable selection/editor if it was removed by this action
      selectedCable: state.selectedCable && removedCableIds.includes(state.selectedCable.id) ? null : state.selectedCable,
      isCablePortEditorOpen: state.selectedCable && removedCableIds.includes(state.selectedCable.id) ? false : state.isCablePortEditorOpen
    });
    requestCanvasCommit();

  },

  replaceEquipment: (placedEquipmentId, newEquipment) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const current = (design.equipment || []).find(eq => eq.id === placedEquipmentId);
    if (!current) return;

    // Determine new port capacity if available
    const newPortCount = newEquipment.specifications?.ports || 0;

    // Drop cables that reference ports beyond new capacity
    const filteredCables = (design.cables || []).filter(cable => {
      const matchFrom = cable.from.match(/^(.+):port:(\d+)$/);
      const matchTo = cable.to.match(/^(.+):port:(\d+)$/);
      if (!matchFrom || !matchTo) return true;
      const [, fromEq, fromPort] = matchFrom;
      const [, toEq, toPort] = matchTo;
      if (fromEq === placedEquipmentId && newPortCount > 0 && parseInt(fromPort) > newPortCount) return false;
      if (toEq === placedEquipmentId && newPortCount > 0 && parseInt(toPort) > newPortCount) return false;
      return true;
    });

    const updatedEquipment = (design.equipment || []).map(eq =>
      eq.id === placedEquipmentId ? { ...eq, equipment: newEquipment } : eq
    );

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id
        ? { ...d, equipment: updatedEquipment, cables: filteredCables, updatedAt: new Date() }
        : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      },
      selectedEquipment: state.selectedEquipment?.id === placedEquipmentId ?
        { ...(state.selectedEquipment as any), equipment: newEquipment } : state.selectedEquipment
    });
    requestCanvasCommit();

  },

  setEquipmentLabel: (equipmentId, label) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const trimmedLabel = label?.trim() || '';
    
    // Check for duplicate equipment labels (case-insensitive) if label is not empty
    if (trimmedLabel !== '') {
      const duplicateEquipment = (design.equipment || []).find(eq => 
        eq.id !== equipmentId && 
        eq.config?.label?.toLowerCase().trim() === trimmedLabel.toLowerCase().trim()
      );
      
      if (duplicateEquipment) {
        console.warn(`Duplicate equipment label detected: "${trimmedLabel}"`);
        // Don't throw error here - let the UI handle the validation and show the alert
        return;
      }
    }

    const updatedEquipment = (design.equipment || []).map(eq => {
      if (eq.id === equipmentId) {
        // If adding a label, clean up the equipment name (remove auto-numbering)
        // If removing a label, keep the current name as is
        let updatedEquipmentName = eq.equipment.name;
        
        if (trimmedLabel !== '') {
          // Clean up the equipment name by removing auto-numbering pattern
          updatedEquipmentName = eq.equipment.name.replace(/ \(\d+\)$/, '');
        }
        
        return { 
          ...eq, 
          equipment: { ...eq.equipment, name: updatedEquipmentName },
          config: { ...eq.config, label: trimmedLabel } 
        };
      }
      return eq;
    });

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, equipment: updatedEquipment, updatedAt: new Date() } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      },
      // Update selectedEquipment if it's the one being modified so UI reflects immediately
      selectedEquipment: state.selectedEquipment?.id === equipmentId
        ? { 
            ...state.selectedEquipment, 
            equipment: { ...state.selectedEquipment.equipment, name: updatedEquipment.find(eq => eq.id === equipmentId)?.equipment.name || state.selectedEquipment.equipment.name },
            config: { ...state.selectedEquipment.config, label: trimmedLabel } 
          }
        : state.selectedEquipment
    });
  },

  updateEquipmentPosition: (equipmentId, position) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedEquipment = (design.equipment || []).map(eq => {
      if (eq.id === equipmentId) {
        return { ...eq, position };
      }
      return eq;
    });

    // Find the updated equipment to update selectedEquipment as well
    const updatedSelectedEquipment = updatedEquipment.find(eq => eq.id === equipmentId);

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, equipment: updatedEquipment } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        // bump updatedAt on mouse up via caller when needed; keep cables live during drag via draggedEquipmentPositions
      },
      selectedEquipment: state.selectedEquipment?.id === equipmentId ? updatedSelectedEquipment || state.selectedEquipment : state.selectedEquipment
    });
    requestCanvasCommit();
  },

  addCable: (from, to, type) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const cable: Cable = {
      id: generateId(),
      from,
      to,
      type
    };
    if (type === 'power' && powerCableGuard(from, to) === 'reject') return;

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id
        ? { ...d, cables: [...(d.cables || []), cable], updatedAt: new Date() }
        : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      }
    });
    requestCanvasCommit();
  },

  addCableWithProperties: (cable) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const existing = uniqueCablesById(design.cables);
    const duplicateLabel = (cable.label?.trim() || '')
      ? existing.find(
          (c) =>
            c.id !== cable.id &&
            c.label?.toLowerCase().trim() === cable.label!.trim().toLowerCase()
        )
      : undefined;
    if (duplicateLabel) return;
    const guard = powerCableGuard(cable.from, cable.to);
    if (guard === 'reject') return;
    const nextCable = guard === 'force-power' ? { ...cable, type: 'power' as const } : cable;

    set({
      currentProject: writeDesignCables(project, design.id, upsertCable(existing, nextCable)),
    });
    requestCanvasCommit();
  },

  setSelectedRack: (rack) => set({ selectedRack: rack }),
  setSelectedEquipment: (equipment) => set({ selectedEquipment: equipment }),
  setSelectedRackEquipment: (rackId, rackEquipment) => set({ 
    selectedRackEquipment: rackEquipment ? { rackId, equipment: rackEquipment.equipment, index: rackEquipment.index } : null 
  }),
  setSelectedCable: (cable) => set({ selectedCable: cable }),
  setCablePortEditorOpen: (open) => set({ isCablePortEditorOpen: open, isCableEditModalOpen: false }),
  setRackConfigOpen: (open) => set({ isRackConfigOpen: open }),
  setEquipmentConfigOpen: (open) => set({ isEquipmentConfigOpen: open }),
  setCableConfigOpen: (open) => set({ isCableConfigOpen: open }),
  setCableEditModalOpen: (open) =>
    set({
      isCableEditModalOpen: false,
      ...(open ? { isCablePortEditorOpen: true } : {}),
    }),
  setSettingsConfigOpen: (open) => set({ isSettingsConfigOpen: open }),
  setIsSettingsPinned: (pinned) => set({ isSettingsPinned: pinned }),
  setFloorplanDrawerOpen: (open) => set({ isFloorplanDrawerOpen: open }),
  setFloorplanSelectionOpen: (open) => set({ isFloorplanSelectionOpen: open }),
  setAddEquipmentOpen: (open) => set({ isAddEquipmentOpen: open }),
  setRackBuilderOpen: (open) => set({ isRackBuilderOpen: open }),
  setShowAPRange: (show) => {
    patchDesignerSetting(set, get, { showAPRange: show });
  },
  setDraggedItem: (item) => set({ draggedItem: item }),

  setFloorplan: (url, width, height) => {
    console.log('setFloorplan called with:', { url: url.substring(0, 50) + '...', width, height });
    const state = get();
    if (!state.currentProject) {
      console.error('No current project found');
      return;
    }

    const updatedProject = {
      ...state.currentProject,
      floorplan: { url, width, height },
      updatedAt: new Date()
    };
    
    console.log('Updated project with floorplan:', updatedProject.floorplan);
    
    set({
      currentProject: updatedProject
    });
    
  },

  setFloorplanData: (data) => {
    const state = get();
    if (!state.currentProject) return;

    set({
      currentProject: {
        ...state.currentProject,
        floorplan: {
          ...(state.currentProject.floorplan || {}),
          ...data,
          updatedAt: new Date()
        },
        updatedAt: new Date()
      }
    });
    
  },

  clearFloorplan: () => {
    const state = get();
    if (!state.currentProject) return;

    const updatedProject = {
      ...state.currentProject,
      floorplan: undefined,
      updatedAt: new Date()
    };
    
    set({
      currentProject: updatedProject
    });
    
  },

  setCanvasMode: (mode) => {
    set({
      canvasMode: mode,
      // Clear floor-plan selection when leaving select
      ...(mode !== 'select'
        ? {
            selectedFloorPlanWallId: null,
            selectedFloorPlanWallIds: [],
            selectedFloorPlanRoomId: null,
            selectedFloorPlanOpeningId: null,
            selectedReferenceImageId: null,
          }
        : {}),
      // Cancel click-to-place queue when leaving place mode
      ...(mode !== 'place' ? { pendingReferenceImages: [] } : {}),
    });
  },

  getFloorPlan: () => {
    const design = get().getCurrentDesign();
    const fp = design?.floorPlan ?? null;
    if (!fp) return null;
    let next = normalizeFloorPlanReferenceImages(fp);
    // Keep display scale fixed so typed lengths (e.g. 5 m) stay screen-sized
    if (next.pixelsPerMeter !== DEFAULT_PIXELS_PER_METER) {
      next = { ...next, pixelsPerMeter: DEFAULT_PIXELS_PER_METER, calibrated: true };
    }
    return next;
  },

  ensureFloorPlan: () => {
    const state = get();
    const design = state.getCurrentDesign();
    const stored = design?.floorPlan;
    if (stored) {
      const normalized = normalizeFloorPlanReferenceImages(stored);
      const needsWrite =
        stored.referenceImage != null ||
        stored.pixelsPerMeter !== DEFAULT_PIXELS_PER_METER ||
        !Array.isArray(stored.referenceImages);
      if (needsWrite) {
        state.updateFloorPlan(
          (fp) => ({
            ...normalizeFloorPlanReferenceImages(fp),
            pixelsPerMeter: DEFAULT_PIXELS_PER_METER,
            calibrated: true,
          }),
          false
        );
      }
      return state.getFloorPlan() || normalized;
    }

    const prefs = state.getUserPreferences();
    const units: 'metric' | 'imperial' =
      state.floorPlanUnits === 'imperial' || prefs?.lastUsedMeasurementSystem === 'imperial'
        ? 'imperial'
        : 'metric';
    const fp = createEmptyFloorPlan(units, DEFAULT_PIXELS_PER_METER);

    if (!state.currentProject) return fp;
    const project = ensureProjectHasDesigns(state.currentProject);
    const designId =
      state.activeDesignId ||
      state.currentDesignId ||
      project.designs?.[0]?.id;
    if (!designId || !project.designs?.length) return fp;

    const updatedDesigns = project.designs.map((d) =>
      d.id === designId ? { ...d, floorPlan: fp, updatedAt: new Date() } : d
    );
    set({
      currentProject: { ...project, designs: updatedDesigns, updatedAt: new Date() },
      currentDesignId: designId,
      activeDesignId: state.activeDesignId || designId,
    });
    return fp;
  },

  updateFloorPlan: (updater, pushHistory = true) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const designId =
      state.activeDesignId ||
      state.currentDesignId ||
      project.designs?.[0]?.id;
    const design = project.designs?.find((d) => d.id === designId);
    if (!design) return;

    const current = design.floorPlan || createEmptyFloorPlan();
    const source = pushHistory
      ? (JSON.parse(JSON.stringify(current)) as DesignFloorPlan)
      : current;
    const next = updater(source);

    const updatedDesigns = (project.designs || []).map((d) =>
      d.id === design.id
        ? {
            ...d,
            floorPlan: next,
            ...(pushHistory ? { updatedAt: new Date() } : {}),
          }
        : d
    );

    const past = pushHistory
      ? [...state.floorPlanHistoryPast.slice(-49), current]
      : state.floorPlanHistoryPast;

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        ...(pushHistory ? { updatedAt: new Date() } : {}),
      },
      floorPlanHistoryPast: past,
      floorPlanHistoryFuture: pushHistory ? [] : state.floorPlanHistoryFuture,
    });
  },

  snapshotFloorPlanForDrag: () => {
    const fp = get().getFloorPlan();
    if (!fp) return;
    const snapshot = JSON.parse(JSON.stringify(fp)) as DesignFloorPlan;
    set({
      floorPlanHistoryPast: [...get().floorPlanHistoryPast.slice(-49), snapshot],
      floorPlanHistoryFuture: [],
    });
  },

  applyFloorPlanLive: (next) => {
    get().updateFloorPlan(() => next, false);
  },

  discardLastFloorPlanSnapshot: () => {
    const past = get().floorPlanHistoryPast;
    if (past.length === 0) return;
    set({ floorPlanHistoryPast: past.slice(0, -1) });
  },

  addWall: (wallInput) => {
    const state = get();
    state.ensureFloorPlan();
    const id = wallInput.id || generateId();
    const wall: Wall = {
      id,
      a: wallInput.a,
      b: wallInput.b,
      thicknessM: wallInput.thicknessM,
    };

    state.updateFloorPlan((fp) => ({
      ...fp,
      walls: [...fp.walls, wall],
    }));

    return id;
  },

  updateWall: (wallId, updates) => {
    get().updateFloorPlan((fp) => ({
      ...fp,
      walls: fp.walls.map((w) => (w.id === wallId ? { ...w, ...updates } : w)),
    }));
  },

  deleteWall: (wallId) => {
    get().deleteWalls([wallId]);
  },

  deleteWalls: (wallIds) => {
    const ids = new Set(wallIds.filter(Boolean));
    if (ids.size === 0) return;
    const absorbedRoomIds: string[] = [];
    let survivorRoomId: string | null = null;
    get().updateFloorPlan((plan) => {
      const remainingWalls = plan.walls.filter((w) => !ids.has(w.id));
      const affected = plan.rooms.filter((r) => r.wallIds.some((id) => ids.has(id)));
      const nextRooms = applyDeletedWallsToRooms(plan.rooms, remainingWalls, ids);
      if (affected.length >= 2) {
        const survivor = nextRooms.find((r) => affected.some((a) => a.id === r.id));
        if (survivor) {
          survivorRoomId = survivor.id;
          for (let i = 0; i < affected.length; i++) {
            if (affected[i].id !== survivor.id) absorbedRoomIds.push(affected[i].id);
          }
        }
      } else if (affected.length === 1) {
        absorbedRoomIds.push(affected[0].id);
      }
      return {
        ...plan,
        walls: remainingWalls,
        openings: plan.openings.filter((o) => !ids.has(o.wallId)),
        rooms: nextRooms,
      };
    });
    const state = get();
    const nextIds = (state.selectedFloorPlanWallIds || []).filter((id) => !ids.has(id));
    const selectedRoomGone =
      !!state.selectedFloorPlanRoomId && absorbedRoomIds.includes(state.selectedFloorPlanRoomId);
    set({
      selectedFloorPlanWallIds: nextIds,
      selectedFloorPlanWallId:
        state.selectedFloorPlanWallId && ids.has(state.selectedFloorPlanWallId)
          ? (nextIds[nextIds.length - 1] ?? null)
          : state.selectedFloorPlanWallId,
      selectedFloorPlanRoomId: selectedRoomGone
        ? survivorRoomId
        : state.selectedFloorPlanRoomId,
      pendingScaleWallId:
        state.pendingScaleWallId && ids.has(state.pendingScaleWallId)
          ? null
          : state.pendingScaleWallId,
    });
  },

  mergeWalls: (idA, idB) => {
    if (!idA || !idB || idA === idB) {
      return { ok: false, reason: 'Select two different walls to merge.' };
    }
    const fp = get().getFloorPlan();
    if (!fp) return { ok: false, reason: 'No floor plan to merge.' };
    const w1 = fp.walls.find((w) => w.id === idA);
    const w2 = fp.walls.find((w) => w.id === idB);
    if (!w1 || !w2) return { ok: false, reason: 'Could not find both walls.' };

    const merged = mergeWallGeometry(w1, w2);
    if ('error' in merged) {
      return {
        ok: false,
        reason:
          merged.error === 'not-colinear'
            ? 'Walls must be in a straight line to merge.'
            : 'Walls must touch at a corner to merge.',
      };
    }

    const newWall: Wall = {
      ...w1,
      a: merged.a,
      b: merged.b,
      thicknessM: w1.thicknessM ?? w2.thicknessM,
    };

    get().updateFloorPlan((plan) => ({
      ...plan,
      walls: plan.walls
        .filter((w) => w.id !== idB)
        .map((w) => (w.id === idA ? newWall : w)),
      openings: plan.openings.map((o) => {
        if (o.wallId === idA) {
          return { ...o, t: remapOpeningParameter(o, w1, newWall) };
        }
        if (o.wallId === idB) {
          return { ...o, wallId: idA, t: remapOpeningParameter(o, w2, newWall) };
        }
        return o;
      }),
      rooms: plan.rooms.map((r) => {
        if (!r.wallIds.includes(idB) && !r.wallIds.includes(idA)) return r;
        const wallIds = Array.from(new Set(r.wallIds.map((id) => (id === idB ? idA : id))));
        return { ...r, wallIds };
      }),
    }));

    set({
      selectedFloorPlanWallId: idA,
      selectedFloorPlanWallIds: [idA],
      selectedFloorPlanRoomId: null,
      selectedFloorPlanOpeningId: null,
    });
    return { ok: true };
  },

  splitWall: (wallId) => {
    if (!wallId) return { ok: false, reason: 'Select a wall to split.' };
    const fp = get().getFloorPlan();
    if (!fp) return { ok: false, reason: 'No floor plan to split.' };
    const result = splitWallAtTJunctions(fp.walls, wallId);
    if ('error' in result) return { ok: false, reason: result.error };

    const ppm = fp.pixelsPerMeter || DEFAULT_PIXELS_PER_METER;
    get().updateFloorPlan((plan) => ({
      ...plan,
      walls: result.walls,
      openings: remapOpeningsForDrag(plan.openings, plan.walls, result.walls, ppm),
      rooms: rebuildRoomPolygons(plan.rooms, result.walls),
    }));
    set({
      selectedFloorPlanWallId: result.segmentIds[0] ?? wallId,
      selectedFloorPlanWallIds: result.segmentIds,
      selectedFloorPlanRoomId: null,
      selectedFloorPlanOpeningId: null,
    });
    return { ok: true };
  },

  addOpening: (openingInput) => {
    const state = get();
    state.ensureFloorPlan();
    const id = openingInput.id || generateId();
    const opening: Opening = {
      id,
      wallId: openingInput.wallId,
      t: openingInput.t,
      widthM: openingInput.widthM ?? DEFAULT_DOOR_WIDTH_M,
      kind: openingInput.kind || 'door',
      hinge: openingInput.hinge ?? 'start',
      swingSide: openingInput.swingSide ?? 1,
    };
    state.updateFloorPlan((fp) => ({
      ...fp,
      openings: [...fp.openings, opening],
    }));
    return id;
  },

  updateOpening: (openingId, updates) => {
    get().updateFloorPlan((fp) => ({
      ...fp,
      openings: fp.openings.map((o) => (o.id === openingId ? { ...o, ...updates } : o)),
    }));
  },

  deleteOpening: (openingId) => {
    get().updateFloorPlan((fp) => ({
      ...fp,
      openings: fp.openings.filter((o) => o.id !== openingId),
    }));
    if (get().selectedFloorPlanOpeningId === openingId) {
      set({ selectedFloorPlanOpeningId: null });
    }
  },

  addRoom: (roomInput) => {
    const state = get();
    state.ensureFloorPlan();
    const id = roomInput.id || generateId();
    const room: Room = {
      id,
      name: roomInput.name,
      polygon: roomInput.polygon,
      wallIds: roomInput.wallIds,
    };
    state.updateFloorPlan((fp) => ({
      ...fp,
      rooms: [...fp.rooms, room],
    }));
    return id;
  },

  updateRoom: (roomId, updates) => {
    get().updateFloorPlan((fp) => ({
      ...fp,
      rooms: fp.rooms.map((r) => (r.id === roomId ? { ...r, ...updates } : r)),
    }));
  },

  deleteRoom: (roomId) => {
    get().updateFloorPlan((fp) => ({
      ...fp,
      rooms: fp.rooms.filter((r) => r.id !== roomId),
    }));
    if (get().selectedFloorPlanRoomId === roomId) {
      set({ selectedFloorPlanRoomId: null });
    }
  },

  setFloorPlanUnits: (units) => {
    get().ensureFloorPlan();
    get().updateFloorPlan((fp) => ({ ...fp, units }));
    patchDesignerSetting(set, get, { floorPlanUnits: units });
  },

  setFloorPlanScale: (_pixelsPerMeter, calibrated = true) => {
    get().ensureFloorPlan();
    get().updateFloorPlan((fp) => ({
      ...fp,
      // Display scale is fixed — ignore callers that try to change it
      pixelsPerMeter: DEFAULT_PIXELS_PER_METER,
      calibrated,
    }));
  },

  calibrateFloorPlanFromWall: (wallId, lengthValue, unit) => {
    // Display scale stays fixed; only units preference may change
    get().ensureFloorPlan();
    get().updateFloorPlan((plan) => ({
      ...plan,
      pixelsPerMeter: DEFAULT_PIXELS_PER_METER,
      calibrated: true,
      units: unit === 'ft' || unit === 'in' ? 'imperial' : 'metric',
    }));
    set({ pendingScaleWallId: null });
    void wallId;
    void lengthValue;
  },

  setPendingScaleWallId: (wallId) => set({ pendingScaleWallId: wallId }),
  setSelectedFloorPlanWallId: (wallId) =>
    set({
      selectedFloorPlanWallId: wallId,
      selectedFloorPlanWallIds: wallId ? [wallId] : [],
      selectedFloorPlanRoomId: null,
      selectedFloorPlanOpeningId: null,
      selectedReferenceImageId: null,
    }),
  selectFloorPlanWall: (wallId, additive = false) => {
    if (!wallId) {
      set({ selectedFloorPlanWallId: null, selectedFloorPlanWallIds: [] });
      return;
    }
    if (additive) {
      const current = get().selectedFloorPlanWallIds;
      const next = current.includes(wallId)
        ? current.filter((id) => id !== wallId)
        : [...current, wallId];
      set({
        selectedFloorPlanWallId: next[next.length - 1] ?? null,
        selectedFloorPlanWallIds: next,
        selectedFloorPlanRoomId: null,
        selectedFloorPlanOpeningId: null,
        selectedReferenceImageId: null,
      });
      return;
    }
    set({
      selectedFloorPlanWallId: wallId,
      selectedFloorPlanWallIds: [wallId],
      selectedFloorPlanRoomId: null,
      selectedFloorPlanOpeningId: null,
      selectedReferenceImageId: null,
    });
  },
  setSelectedFloorPlanRoomId: (roomId) =>
    set({
      selectedFloorPlanRoomId: roomId,
      selectedFloorPlanWallId: null,
      selectedFloorPlanWallIds: [],
      selectedFloorPlanOpeningId: null,
      selectedReferenceImageId: null,
    }),
  setSelectedFloorPlanOpeningId: (openingId) =>
    set({
      selectedFloorPlanOpeningId: openingId,
      selectedFloorPlanWallId: null,
      selectedFloorPlanWallIds: [],
      selectedFloorPlanRoomId: null,
      selectedReferenceImageId: null,
    }),

  setSelectedReferenceImageId: (imageId) =>
    set({
      selectedReferenceImageId: imageId,
      selectedFloorPlanWallId: null,
      selectedFloorPlanWallIds: [],
      selectedFloorPlanRoomId: null,
      selectedFloorPlanOpeningId: null,
    }),

  queueFloorPlanReferenceImages: (images) => {
    if (images.length === 0) return;
    get().ensureFloorPlan();
    set({
      pendingReferenceImages: [...get().pendingReferenceImages, ...images],
      canvasMode: 'place',
      selectedReferenceImageId: null,
      selectedFloorPlanWallId: null,
      selectedFloorPlanWallIds: [],
      selectedFloorPlanRoomId: null,
      selectedFloorPlanOpeningId: null,
      draggedItem: null,
      isRackPlacementMode: false,
      pendingRackPlacement: null,
    });
  },

  placeNextPendingReferenceImage: (position) => {
    const queue = get().pendingReferenceImages;
    if (queue.length === 0) return null;
    const [next, ...rest] = queue;
    set({ pendingReferenceImages: rest });

    const width = next.width;
    const height = next.height;
    const ids = get().addFloorPlanReferenceImages([
      {
        ...next,
        x: position.x - width / 2,
        y: position.y - height / 2,
      },
    ]);
    const id = ids[0] ?? null;
    set({
      selectedReferenceImageId: rest.length === 0 ? id : null,
      canvasMode: rest.length === 0 ? 'select' : 'place',
      selectedFloorPlanWallId: null,
      selectedFloorPlanWallIds: [],
      selectedFloorPlanRoomId: null,
      selectedFloorPlanOpeningId: null,
    });
    return id;
  },

  cancelPendingReferenceImages: () => {
    set({ pendingReferenceImages: [] });
  },

  addFloorPlanReferenceImages: (images) => {
    get().ensureFloorPlan();
    const ids: string[] = [];
    get().updateFloorPlan((fp) => {
      const normalized = normalizeFloorPlanReferenceImages(fp);
      const existing = [...(normalized.referenceImages ?? [])];
      const added = images.map((img, i) => {
        const id = img.id || generateId();
        ids.push(id);
        return {
          id,
          url: img.url,
          name: img.name,
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
          x: img.x ?? 0,
          y: img.y ?? 0,
          width: img.width,
          height: img.height,
          opacity: img.opacity ?? 0.45,
        } satisfies FloorPlanReferenceImage;
      });
      const { referenceImage: _legacy, ...rest } = normalized;
      return {
        ...rest,
        referenceImages: [...existing, ...added],
      };
    });
    return ids;
  },

  updateFloorPlanReferenceImage: (id, patch, pushHistory = true) => {
    get().updateFloorPlan((fp) => {
      const normalized = normalizeFloorPlanReferenceImages(fp);
      const { referenceImage: _legacy, ...rest } = normalized;
      return {
        ...rest,
        referenceImages: (normalized.referenceImages ?? []).map((img) =>
          img.id === id ? { ...img, ...patch } : img
        ),
      };
    }, pushHistory);
  },

  removeFloorPlanReferenceImage: (id) => {
    get().updateFloorPlan((fp) => {
      const normalized = normalizeFloorPlanReferenceImages(fp);
      const { referenceImage: _legacy, ...rest } = normalized;
      return {
        ...rest,
        referenceImages: (normalized.referenceImages ?? []).filter((img) => img.id !== id),
      };
    });
    if (get().selectedReferenceImageId === id) {
      set({ selectedReferenceImageId: null });
    }
  },

  clearFloorPlanReferenceImages: () => {
    get().updateFloorPlan((fp) => {
      const { referenceImage: _legacy, ...rest } = normalizeFloorPlanReferenceImages(fp);
      return { ...rest, referenceImages: [] };
    });
    set({ selectedReferenceImageId: null });
  },

  setFloorPlanReferenceImage: (image) => {
    if (!image) {
      get().clearFloorPlanReferenceImages();
      return;
    }
    get().clearFloorPlanReferenceImages();
    get().addFloorPlanReferenceImages([
      {
        url: image.url,
        naturalWidth: image.width,
        naturalHeight: image.height,
        x: 0,
        y: 0,
        width: image.width,
        height: image.height,
        opacity: image.opacity ?? 0.45,
      },
    ]);
  },

  setEquipmentPanelPreviewHidden: (hidden) => {
    const state = get();
    if (state.equipmentPanelPreviewHidden === hidden) return;
    const project = state.currentProject;
    const activeId = state.activeDesignId || state.currentDesignId;
    set({
      equipmentPanelPreviewHidden: hidden,
      ...(project && activeId
        ? {
            currentProject: {
              ...project,
              designs: (project.designs || []).map((d) =>
                d.id === activeId ? { ...d, equipmentPanelPreviewHidden: hidden } : d
              ),
              updatedAt: new Date(),
            },
          }
        : {}),
    });
    persistDesignerPrefsFromState(get());
  },

  setBrowserGroupVisible: (group, visible) => {
    const state = get();
    if (!state.currentProject) return;
    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;
    const updatedDesigns = (project.designs || []).map((d) =>
      d.id === design.id
        ? {
            ...d,
            browserVisibility: withBrowserGroupVisible(d.browserVisibility, group, visible),
            updatedAt: new Date(),
          }
        : d
    );
    set({
      currentProject: { ...project, designs: updatedDesigns, updatedAt: new Date() },
    });
  },

  setBrowserEquipmentTypeVisible: (typeKey, visible) => {
    const state = get();
    if (!state.currentProject) return;
    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;
    const updatedDesigns = (project.designs || []).map((d) =>
      d.id === design.id
        ? {
            ...d,
            browserVisibility: withEquipmentTypeVisible(d.browserVisibility, typeKey, visible),
            updatedAt: new Date(),
          }
        : d
    );
    set({
      currentProject: { ...project, designs: updatedDesigns, updatedAt: new Date() },
    });
  },

  setItemHidden: (kind, id, hidden) => {
    const state = get();
    if (!state.currentProject) return;
    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const patchDesign = (updater: (d: Design) => Design) => {
      const updatedDesigns = (project.designs || []).map((d) =>
        d.id === design.id ? { ...updater(d), updatedAt: new Date() } : d
      );
      set({
        currentProject: { ...project, designs: updatedDesigns, updatedAt: new Date() },
      });
    };

    switch (kind) {
      case 'equipment':
        patchDesign((d) => ({
          ...d,
          equipment: d.equipment.map((e) => (e.id === id ? { ...e, hidden } : e)),
        }));
        break;
      case 'cable':
        patchDesign((d) => ({
          ...d,
          cables: d.cables.map((c) => (c.id === id ? { ...c, hidden } : c)),
        }));
        break;
      case 'rack':
        patchDesign((d) => ({
          ...d,
          racks: d.racks.map((r) => (r.id === id ? { ...r, hidden } : r)),
        }));
        break;
      case 'box':
        patchDesign((d) => ({
          ...d,
          boxes: (d.boxes || []).map((b: CanvasBox) => (b.id === id ? { ...b, hidden } : b)),
        }));
        break;
      case 'label':
        patchDesign((d) => ({
          ...d,
          labels: (d.labels || []).map((l: CanvasLabel) => (l.id === id ? { ...l, hidden } : l)),
        }));
        break;
      case 'wall':
        get().ensureFloorPlan();
        get().updateFloorPlan((fp) => ({
          ...fp,
          walls: fp.walls.map((w) => (w.id === id ? { ...w, hidden } : w)),
        }));
        break;
      case 'room':
        get().ensureFloorPlan();
        get().updateFloorPlan((fp) => ({
          ...fp,
          rooms: fp.rooms.map((r) => (r.id === id ? { ...r, hidden } : r)),
        }));
        break;
      case 'door':
        get().ensureFloorPlan();
        get().updateFloorPlan((fp) => ({
          ...fp,
          openings: fp.openings.map((o) => (o.id === id ? { ...o, hidden } : o)),
        }));
        break;
      case 'referenceImage':
        get().updateFloorPlanReferenceImage(id, { hidden }, true);
        break;
      default:
        break;
    }
  },

  isItemVisible: (kind, id) => {
    const design = get().getCurrentDesign();
    return isItemEffectivelyVisible(design, kind, id);
  },

  undoFloorPlan: () => {
    const state = get();
    if (state.floorPlanHistoryPast.length === 0) return;
    const project = state.currentProject;
    if (!project) return;
    const ensured = ensureProjectHasDesigns(project);
    const design = state.getCurrentDesign();
    if (!design?.floorPlan) return;

    const previous = state.floorPlanHistoryPast[state.floorPlanHistoryPast.length - 1];
    const past = state.floorPlanHistoryPast.slice(0, -1);
    const future = [design.floorPlan, ...state.floorPlanHistoryFuture].slice(0, 50);

    const updatedDesigns = (ensured.designs || []).map((d) =>
      d.id === design.id ? { ...d, floorPlan: previous, updatedAt: new Date() } : d
    );
    set({
      currentProject: { ...ensured, designs: updatedDesigns, updatedAt: new Date() },
      floorPlanHistoryPast: past,
      floorPlanHistoryFuture: future,
    });
  },

  redoFloorPlan: () => {
    const state = get();
    if (state.floorPlanHistoryFuture.length === 0) return;
    const project = state.currentProject;
    if (!project) return;
    const ensured = ensureProjectHasDesigns(project);
    const design = state.getCurrentDesign();
    if (!design?.floorPlan) return;

    const next = state.floorPlanHistoryFuture[0];
    const future = state.floorPlanHistoryFuture.slice(1);
    const past = [...state.floorPlanHistoryPast, design.floorPlan].slice(-50);

    const updatedDesigns = (ensured.designs || []).map((d) =>
      d.id === design.id ? { ...d, floorPlan: next, updatedAt: new Date() } : d
    );
    set({
      currentProject: { ...ensured, designs: updatedDesigns, updatedAt: new Date() },
      floorPlanHistoryPast: past,
      floorPlanHistoryFuture: future,
    });
  },

  getPixelsPerMeter: () => DEFAULT_PIXELS_PER_METER,

  canvasDistanceToMeters: (px) => {
    return px > 0 ? px / DEFAULT_PIXELS_PER_METER : 0;
  },

  migrateLegacyFloorplanIntoDesign: () => {
    const state = get();
    if (!state.currentProject) return false;
    const design = state.getCurrentDesign();
    if (!design) return false;

    // Already has walls — nothing to do
    if (design.floorPlan?.walls?.length) return false;

    const project = ensureProjectHasDesigns(state.currentProject);
    let floorPlan = design.floorPlan || createEmptyFloorPlan();
    let migrated = false;

    // Prefer legacy vector floorplans from cache / localStorage
    const legacyList = state.getFloorplans() || [];
    if (legacyList.length > 0) {
      const legacy = legacyList[0] as FloorplanData;
      floorPlan = migrateFloorplanDataToDesignFloorPlan(legacy);
      migrated = floorPlan.walls.length > 0;
    }

    // Use project raster as reference image if present
    if (project.floorplan?.url) {
      floorPlan = {
        ...floorPlan,
        referenceImages: [
          {
            id: 'ref-legacy-project',
            url: project.floorplan.url,
            naturalWidth: project.floorplan.width || 1920,
            naturalHeight: project.floorplan.height || 1080,
            x: 0,
            y: 0,
            width: project.floorplan.width || 1920,
            height: project.floorplan.height || 1080,
            opacity: 0.45,
          },
        ],
      };
      migrated = true;
    }

    if (!migrated) return false;

    const updatedDesigns = (project.designs || []).map((d) =>
      d.id === design.id ? { ...d, floorPlan, updatedAt: new Date() } : d
    );
    set({
      currentProject: { ...project, designs: updatedDesigns, updatedAt: new Date() },
    });
    return true;
  },

  getUserPreferences: () => {
    try {
      const stored = localStorage.getItem('netprobuilder-user-preferences');
      return stored ? JSON.parse(stored) : {
        lastUsedMeasurementSystem: 'imperial',
        lastUsedPrimaryUnit: 'inches'
      };
    } catch {
      return {
        lastUsedMeasurementSystem: 'imperial',
        lastUsedPrimaryUnit: 'inches'
      };
    }
  },

  setUserPreferences: (preferences) => {
    try {
      localStorage.setItem('netprobuilder-user-preferences', JSON.stringify(preferences));
    } catch (error) {
      console.warn('Failed to save user preferences:', error);
    }
  },

  fetchAndCacheFloorplans: async (collectionId: string) => {
    try {
      const rows = await fetchFloorplansForCollection(collectionId);
      const floorplans = rows.map((row) => {
        const url = row.overlay_path ? overlayPublicUrl(row.overlay_path) : undefined;
        return dbRowToFloorplan(row, url);
      });
      set({ cachedFloorplans: floorplans });
    } catch (err) {
      console.warn('[fetchAndCacheFloorplans]', err);
    }
  },

  saveFloorplan: (floorplan) => {
    const collectionId = getActiveCollectionId();
    const user = useAuthStore.getState().user;

    // Always persist to localStorage as a fallback / cache
    try {
      const floorplans = JSON.parse(localStorage.getItem('netprobuilder-floorplans') || '[]');
      const resolvedId = floorplan.id || generateId();
      const existingIndex = floorplans.findIndex((fp: any) => fp.id === resolvedId);

      if (existingIndex >= 0) {
        const preservedCreatedAt = floorplans[existingIndex].createdAt || new Date().toISOString();
        floorplans[existingIndex] = {
          ...floorplan,
          id: resolvedId,
          createdAt: preservedCreatedAt,
          updatedAt: new Date().toISOString()
        };
      } else {
        floorplans.push({
          ...floorplan,
          id: resolvedId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      
      localStorage.setItem('netprobuilder-floorplans', JSON.stringify(floorplans));
    } catch (error) {
      console.warn('Failed to save floorplan to localStorage:', error);
    }

    // If authenticated and we have an active collection, persist to Supabase too.
    if (user && collectionId) {
      upsertFloorplanRecord(collectionId, floorplan).then((row) => {
        // Update the in-memory cache so getFloorplans() stays current.
        const updated = dbRowToFloorplan(row, row.overlay_path ? overlayPublicUrl(row.overlay_path) : undefined);
        set((s) => {
          const existing = s.cachedFloorplans.findIndex((fp) => fp.id === updated.id || (fp as any)._dbId === row.id);
          if (existing >= 0) {
            const copy = [...s.cachedFloorplans];
            copy[existing] = updated;
            return { cachedFloorplans: copy };
          }
          return { cachedFloorplans: [...s.cachedFloorplans, updated] };
        });
      }).catch((err) => console.warn('[saveFloorplan cloud]', err));
    }
  },

  loadFloorplan: (floorplanId) => {
    // Prefer the in-memory Supabase cache when available.
    const cached = get().cachedFloorplans;
    if (cached.length > 0) {
      const found = cached.find((fp) => fp.id === floorplanId || (fp as any)._dbId === floorplanId);
      if (found) return found;
    }

    // Fallback: localStorage
    try {
      const floorplans = JSON.parse(localStorage.getItem('netprobuilder-floorplans') || '[]');
      const floorplan = floorplans.find((fp: any) => fp.id === floorplanId);
      if (floorplan) {
        return {
          ...floorplan,
          createdAt: floorplan.createdAt ? new Date(floorplan.createdAt) : new Date(),
          updatedAt: floorplan.updatedAt ? new Date(floorplan.updatedAt) : new Date()
        };
      }
      return null;
    } catch (error) {
      console.warn('Failed to load floorplan:', error);
      return null;
    }
  },

  deleteFloorplan: (floorplanId) => {
    // Remove from localStorage
    try {
      const floorplans = JSON.parse(localStorage.getItem('netprobuilder-floorplans') || '[]');
      localStorage.setItem('netprobuilder-floorplans', JSON.stringify(
        floorplans.filter((fp: any) => fp.id !== floorplanId)
      ));
    } catch (error) {
      console.warn('Failed to delete floorplan from localStorage:', error);
    }

    // Remove from Supabase cache + DB
    const cached = get().cachedFloorplans;
    const target = cached.find((fp) => fp.id === floorplanId || (fp as any)._dbId === floorplanId);
    if (target) {
      set({ cachedFloorplans: cached.filter((fp) => fp !== target) });
      const dbId = (target as any)._dbId as string | undefined;
      if (dbId) {
        deleteFloorplanRecord(dbId).catch((err) => console.warn('[deleteFloorplan cloud]', err));
      }
    }
  },

  getFloorplans: () => {
    // Return from Supabase cache if populated; fall back to localStorage.
    const cached = get().cachedFloorplans;
    if (cached.length > 0) return cached;

    try {
      const floorplans = JSON.parse(localStorage.getItem('netprobuilder-floorplans') || '[]');
      return floorplans.map((fp: any) => ({
        ...fp,
        createdAt: fp.createdAt ? new Date(fp.createdAt) : new Date(),
        updatedAt: fp.updatedAt ? new Date(fp.updatedAt) : new Date()
      }));
    } catch {
      return [];
    }
  },

  setCableRunMode: (active) => set({ 
    isCableRunMode: active, 
    selectedSourceEquipment: null 
  }),

  setCableRunRouting: (routing) => {
    patchDesignerSetting(set, get, { cableRunRouting: routing === 'flexi' ? 'flexi' : 'straight' });
  },
  
  setSelectedSourceEquipment: (equipment) => set({ selectedSourceEquipment: equipment }),
  
  setHoveredCableId: (cableId) => set({ hoveredCableId: cableId }),
  setHoveredEquipmentId: (equipmentId) => set({ hoveredEquipmentId: equipmentId }),
  setHoveredPortIdDuringCableDrag: (portId) => set({ hoveredPortIdDuringCableDrag: portId }),
  setCanvasPan: (panX, panY) => {
    set({ canvasPanX: panX, canvasPanY: panY });
    persistCanvasCameraFromState(get());
  },

  setCanvasZoom: (zoom) => {
    set({ canvasZoom: clampCanvasZoom(zoom) });
    persistCanvasCameraFromState(get());
  },

  setFloorplanPan: (panX, panY) => {
    set({ floorplanPanX: panX, floorplanPanY: panY });
  },

    createCableRun: (fromId, toId, type, label, subtype) => {
    const state = get();
    if (!state.currentProject) return;
    if (!fromId || !toId || fromId === toId) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const existing = uniqueCablesById(design.cables);
    const already = findCableBetween(existing, fromId, toId);
    if (already) {
      set({
        currentProject: writeDesignCables(project, design.id, existing),
        selectedCable: already,
        pendingNewCableId: null,
        isCablePortEditorOpen: true,
        isCableEditModalOpen: false,
        isCableRunMode: false,
        selectedSourceEquipment: null,
      });
      return;
    }

    const estimatedLength = estimateCableLengthMeters(design, fromId, toId);
    const guard = powerCableGuard(fromId, toId);
    if (guard === 'reject') return;
    const cableType = guard === 'force-power' ? 'power' : (type || 'ethernet');

    const cable: Cable = {
      id: generateId(),
      from: fromId,
      to: toId,
      type: cableType,
      subtype: cableType === 'ethernet' ? (subtype || '') : '',
      label: label || `${cableType.charAt(0).toUpperCase() + cableType.slice(1)} Cable`,
      routing: state.cableRunRouting === 'flexi' ? 'flexi' : 'straight',
      ...(estimatedLength !== undefined ? { length: estimatedLength } : {}),
    };

    set({
      currentProject: writeDesignCables(project, design.id, [...existing, cable]),
      selectedCable: cable,
      pendingNewCableId: cable.id,
      isCablePortEditorOpen: true,
      isCableEditModalOpen: false,
      isCableRunMode: false,
      selectedSourceEquipment: null,
    });
  },

  confirmPendingCable: () => {
    set({ pendingNewCableId: null });
  },

  discardPendingCable: () => {
    const state = get();
    const pendingId = state.pendingNewCableId;
    if (!pendingId) {
      set({
        isCableEditModalOpen: false,
        isCablePortEditorOpen: false,
        selectedCable: state.selectedCable,
      });
      return;
    }
    if (!state.currentProject) {
      set({ pendingNewCableId: null, isCableEditModalOpen: false, isCablePortEditorOpen: false, selectedCable: null });
      return;
    }
    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) {
      set({ pendingNewCableId: null, isCableEditModalOpen: false, isCablePortEditorOpen: false, selectedCable: null });
      return;
    }
    const nextCables = uniqueCablesById(design.cables).filter((c) => c.id !== pendingId);
    set({
      currentProject: writeDesignCables(project, design.id, nextCables),
      pendingNewCableId: null,
      selectedCable: null,
      isCableEditModalOpen: false,
      isCablePortEditorOpen: false,
      isCableConfigOpen: false,
    });
  },

  updateCableRun: (cableId, updates) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    // Check for duplicate cable labels (case-insensitive) if label is being updated
    if (updates.label !== undefined) {
      const trimmedLabel = updates.label.trim();
      if (trimmedLabel !== '') {
        const duplicateCable = (design.cables || []).find(c => 
          c.id !== cableId && 
          c.label?.toLowerCase().trim() === trimmedLabel.toLowerCase().trim()
        );
        
        if (duplicateCable) {
          console.warn(`Duplicate cable label detected: "${trimmedLabel}"`);
          // Don't throw error here - let the UI handle the validation and show the alert
          return;
        }
      }
    }

    const currentForGuard = uniqueCablesById(design.cables).find((c) => c.id === cableId);
    if (currentForGuard) {
      const from = updates.from ?? currentForGuard.from;
      const to = updates.to ?? currentForGuard.to;
      if (powerCableGuard(from, to) === 'reject') return;
    }

    const updatedCables = uniqueCablesById(design.cables).map(cable =>
      cable.id === cableId ? { ...cable, ...updates } : cable
    );

    // Update selected cable if it's the one being modified
    const updatedSelectedCable = state.selectedCable?.id === cableId 
      ? { ...state.selectedCable, ...updates } 
      : state.selectedCable;

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, cables: updatedCables, updatedAt: new Date() } : d
    );

    set({
      currentProject: writeDesignCables(project, design.id, updatedCables),
      selectedCable: updatedSelectedCable
    });
  },

  setAllCableRouting: (routing) => {
    const state = get();
    if (!state.currentProject) return 0;
    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return 0;

    const nextRouting: CableRouting = routing === 'flexi' ? 'flexi' : 'straight';
    let changed = 0;
    const updatedCables: Cable[] = uniqueCablesById(design.cables).map((cable) => {
      const current: CableRouting = cable.routing === 'flexi' ? 'flexi' : 'straight';
      if (current === nextRouting) return cable;
      changed += 1;
      return { ...cable, routing: nextRouting };
    });

    if (changed === 0) return 0;

    set({
      currentProject: writeDesignCables(project, design.id, updatedCables),
      selectedCable:
        state.selectedCable && state.selectedCable.routing !== nextRouting
          ? { ...state.selectedCable, routing: nextRouting }
          : state.selectedCable,
    });
    persistWorkingCopyNow(get());
    requestCanvasCommit();
    get().saveProject();
    return changed;
  },

  remapPlacedEquipmentPortNumbers: (placedId, remaps, options) => {
    if (!placedId) return;
    const state = get();
    if (!state.currentProject) return;
    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const rewriteOpts =
      options?.nextLayout != null
        ? { nextLayout: options.nextLayout, detachOrphans: options.detachOrphans !== false }
        : undefined;

    let cablesChanged = false;
    const updatedCables = uniqueCablesById(design.cables).map((cable) => {
      const from = rewritePortEndpoint(cable.from, placedId, remaps || [], rewriteOpts);
      const to = rewritePortEndpoint(cable.to, placedId, remaps || [], rewriteOpts);
      if (from === cable.from && to === cable.to) return cable;
      cablesChanged = true;
      return { ...cable, from, to };
    });

    let labelsChanged = false;
    const updatedEquipment = (design.equipment || []).map((eq) => {
      if (eq.id !== placedId) return eq;
      const current = eq.config?.portCustomLabels;
      const remapped = remapPortCustomLabels(current, remaps || []);
      if (remapped === current) return eq;
      labelsChanged = true;
      return { ...eq, config: { ...(eq.config || {}), portCustomLabels: remapped } };
    });

    if (!cablesChanged && !labelsChanged) return;

    const nextCables = cablesChanged ? updatedCables : uniqueCablesById(design.cables);
    const nextEquipment = labelsChanged ? updatedEquipment : design.equipment;

    const updatedSelectedCable = state.selectedCable
      ? nextCables.find((c) => c.id === state.selectedCable?.id) || state.selectedCable
      : state.selectedCable;
    const updatedSelectedEquipment = state.selectedEquipment?.id === placedId
      ? nextEquipment.find((eq) => eq.id === placedId) || state.selectedEquipment
      : state.selectedEquipment;
    const updatedSelectedForPortView = state.selectedEquipmentForPortView?.id === placedId
      ? nextEquipment.find((eq) => eq.id === placedId) || state.selectedEquipmentForPortView
      : state.selectedEquipmentForPortView;

    const updatedDesigns = (project.designs || []).map((d) =>
      d.id === design.id
        ? { ...d, cables: nextCables, equipment: nextEquipment, updatedAt: new Date() }
        : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date(),
      },
      selectedCable: updatedSelectedCable,
      selectedEquipment: updatedSelectedEquipment,
      selectedEquipmentForPortView: updatedSelectedForPortView as any,
    });
    requestCanvasCommit();
  },

  deleteCableRun: (cableId) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedCables = uniqueCablesById(design.cables).filter(
      cable => cable.id !== cableId
    );

    set({
      currentProject: writeDesignCables(project, design.id, updatedCables),
      selectedCable: state.selectedCable?.id === cableId ? null : state.selectedCable,
      isCableConfigOpen: state.selectedCable?.id === cableId ? false : state.isCableConfigOpen,
      isCableEditModalOpen: false,
      isCablePortEditorOpen: state.selectedCable?.id === cableId ? false : state.isCablePortEditorOpen,
      pendingNewCableId: state.pendingNewCableId === cableId ? null : state.pendingNewCableId,
    });
    requestCanvasCommit();
  },

  clearAllCableRuns: () => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedDesigns = (project.designs || []).map(d =>
      d.id === design.id ? { ...d, cables: [], updatedAt: new Date() } : d
    );

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        updatedAt: new Date()
      },
      selectedCable: null,
      isCableConfigOpen: false,
      isCablePortEditorOpen: false,
      hoveredCableId: null,
    });
  },

  setDraggedEquipmentPosition: (equipmentId, position) => {
    set(state => ({
      draggedEquipmentPositions: {
        ...state.draggedEquipmentPositions,
        [equipmentId]: position
      }
    }));
  },

  clearDraggedEquipmentPosition: (equipmentId) => {
    set(state => {
      const newPositions = { ...state.draggedEquipmentPositions };
      delete newPositions[equipmentId];
      return { draggedEquipmentPositions: newPositions };
    });
  },

  setEquipmentDragActive: (active) => {
    set({ isEquipmentDragActive: active });
  },

  setRackPlacementMode: (active, pendingRack) => {
    set({
      isRackPlacementMode: active,
      pendingRackPlacement: active ? pendingRack || null : null
    });
  },

  placeRackAtPosition: (position) => {
    const state = get();
    if (!state.currentProject || !state.pendingRackPlacement) return;

    const { size, equipment, name, siteId } = state.pendingRackPlacement;

    // Place into the current design (same path as addRack) — canvas reads design.racks
    get().addRack(size, position, equipment, name, siteId ?? null);

    set({
      isRackPlacementMode: false,
      pendingRackPlacement: null
    });
  },

  setSnapToGrid: (enabled) => {
    patchDesignerSetting(set, get, enabled ? { snapToGrid: true, showGrid: true } : { snapToGrid: false });
  },

  setSnapToGridTarget: (target) => {
    const next =
      target === 'walls' || target === 'both' || target === 'icons' ? target : 'icons';
    patchDesignerSetting(set, get, { snapToGridTarget: next });
  },

  setGridSize: (size) => {
    const next = Math.max(1, Math.min(200, Math.round(Number(size) || 10)));
    patchDesignerSetting(set, get, { gridSize: next });
  },

  setIconGridCells: (cells) => {
    const next = parseIconGridCells(cells) ?? DEFAULT_ICON_GRID_CELLS;
    patchDesignerSetting(set, get, { iconGridCells: next });
  },

  setShowGrid: (show) => {
    patchDesignerSetting(set, get, { showGrid: show });
  },

  setShowLegend: (show) => {
    patchDesignerSetting(set, get, { showLegend: show });
  },

  setAlwaysShowExpandedEquipment: (show) => {
    patchDesignerSetting(set, get, { alwaysShowExpandedEquipment: show });
  },

  setPortsDisplayMode: (mode: 'none' | 'full' | string) => {
    // Only allow 'none' or 'full', migrate 'mini' to 'full' (for backward compatibility)
    const validMode = (mode === 'mini' || mode === 'full' || mode === 'none')
      ? (mode === 'mini' ? 'full' : mode)
      : 'none';
    patchDesignerSetting(set, get, { portsDisplayMode: validMode as 'none' | 'full' });
  },
  setCanvasBackground: (mode) => {
    patchDesignerSetting(set, get, { canvasBackground: mode === 'dark' ? 'dark' : 'light' });
  },
  setPanelVisualization: (mode) => {
    patchDesignerSetting(set, get, {
      panelVisualization: mode === 'minimalist' ? 'minimalist' : 'shaded',
    });
  },
  setWallDragMode: (mode) => {
    const safeMode: WallDragMode = mode === 'extend' ? 'extend' : 'default';
    patchDesignerSetting(set, get, { wallDragMode: safeMode });
  },

  setCurrency: (currency) => {
    const valid = ['USD', 'ZAR', 'GBP'] as const;
    const safeCurrency = (valid as readonly string[]).includes(currency) ? currency : 'ZAR';
    set({ currency: safeCurrency as ProjectState['currency'] });
    persistDesignerPrefsFromState(get());
  },
  setAppTheme: (theme) => {
    const safeTheme = theme === 'dark' ? 'dark' : 'default';
    set({ appTheme: safeTheme });
    persistDesignerPrefsFromState(get());
  },
  setAppUIFont: (font) => {
    const valid: AppUIFont[] = ['inter', 'geist', 'plus-jakarta'];
    const safe = valid.includes(font as AppUIFont) ? (font as AppUIFont) : 'inter';
    set({ appUIFont: safe });
    applyAppUIFont(safe);
    persistDesignerPrefsFromState(get());
  },
  setShowCanvasToolbar: (show) => {
    const state = get();
    set({ showCanvasToolbar: show });
    const proj = state.currentProject as any;
    if (proj) {
      set({
        currentProject: {
          ...proj,
          settings: { ...(proj.settings || {}), showCanvasToolbar: show }
        }
      });
    }
  },
  setShowDesignInfoOverlay: (show) => {
    patchDesignerSetting(set, get, { showDesignInfoOverlay: show });
  },
  setShowMinimap: (show) => {
    patchDesignerSetting(set, get, { showMinimap: show });
  },
  setShowManufacturerInEquipmentList: (show) => {
    patchDesignerSetting(set, get, { showManufacturerInEquipmentList: show });
  },
  setExpandBadgesOnHover: (show) => {
    patchDesignerSetting(set, get, { expandBadgesOnHover: show });
  },
  setColourizePorts: (enabled) => {
    patchDesignerSetting(set, get, { colourizePorts: enabled });
  },
  setEquipmentPanelAdvancedDefault: (enabled) => {
    patchDesignerSetting(set, get, { equipmentPanelAdvancedDefault: enabled });
  },

  // Existing infrastructure management actions
  toggleEquipmentExistingStatus: (equipmentId) => {
    const state = get();
    if (!state.currentProject) return;

    const updatedEquipment = state.currentProject.equipment.map(eq => {
      if (eq.id === equipmentId) {
        return { ...eq, isExisting: !eq.isExisting };
      }
      return eq;
    });

    // Update selected references if they point at this equipment
    const updatedSelectedEquipment = state.selectedEquipment?.id === equipmentId
      ? updatedEquipment.find(eq => eq.id === equipmentId) || state.selectedEquipment
      : state.selectedEquipment;
    const updatedSelectedForPortView = state.selectedEquipmentForPortView?.id === equipmentId
      ? updatedEquipment.find(eq => eq.id === equipmentId) || state.selectedEquipmentForPortView
      : state.selectedEquipmentForPortView;

    set({
      currentProject: {
        ...state.currentProject,
        equipment: updatedEquipment,
      },
      selectedEquipment: updatedSelectedEquipment as any,
      selectedEquipmentForPortView: updatedSelectedForPortView as any
    });

  },

  toggleRackExistingStatus: (rackId) => {
    const state = get();
    if (!state.currentProject) return;

    const updatedRacks = state.currentProject.racks.map(rack => {
      if (rack.id === rackId) {
        const isExisting = !rack.isExisting;
        // When marking rack as existing, also mark all rack-mounted equipment as existing
        const updatedEquipment = rack.equipment.map(req => ({
          ...req,
          equipment: { ...req.equipment, isExisting }
        }));
        
        return { ...rack, isExisting, equipment: updatedEquipment };
      }
      return rack;
    });

    set({
      currentProject: {
        ...state.currentProject,
        racks: updatedRacks,
      }
    });

  },

  toggleCableExistingStatus: (cableId) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedCables = uniqueCablesById(design.cables).map(cable => {
      if (cable.id === cableId) {
        return { ...cable, isExisting: !cable.isExisting };
      }
      return cable;
    });

    const updatedSelectedCable = state.selectedCable && state.selectedCable.id === cableId
      ? updatedCables.find(c => c.id === cableId) || state.selectedCable
      : state.selectedCable;

    set({
      currentProject: writeDesignCables(project, design.id, updatedCables),
      selectedCable: updatedSelectedCable as Cable | null
    });
  },

  setEquipmentExistingStatus: (equipmentId, isExisting) => {
    const state = get();
    if (!state.currentProject) return;

    const updatedEquipment = state.currentProject.equipment.map(eq => {
      if (eq.id === equipmentId) {
        return { ...eq, isExisting };
      }
      return eq;
    });

    set({
      currentProject: {
        ...state.currentProject,
        equipment: updatedEquipment,
      }
    });

  },

  setRackExistingStatus: (rackId, isExisting) => {
    const state = get();
    if (!state.currentProject) return;

    const updatedRacks = state.currentProject.racks.map(rack => {
      if (rack.id === rackId) {
        // When marking rack as existing, also mark all rack-mounted equipment as existing
        const updatedEquipment = rack.equipment.map(req => ({
          ...req,
          equipment: { ...req.equipment, isExisting }
        }));
        
        return { ...rack, isExisting, equipment: updatedEquipment };
      }
      return rack;
    });

    set({
      currentProject: {
        ...state.currentProject,
        racks: updatedRacks,
      }
    });

  },

  setCableExistingStatus: (cableId, isExisting) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const design = state.getCurrentDesign();
    if (!design) return;

    const updatedCables = uniqueCablesById(design.cables).map(cable => {
      if (cable.id === cableId) {
        return { ...cable, isExisting };
      }
      return cable;
    });

    set({
      currentProject: writeDesignCables(project, design.id, updatedCables),
    });
  },

  setExistingMode: (enabled) => {
    set({ isExistingMode: enabled });
  },

  updateAutosaveSettings: (settings) => {
    const state = get();
    const newSettings = { ...state.autosaveSettings, ...settings };
    
    set({ autosaveSettings: newSettings });
    persistDesignerPrefsFromState(get());
    
    // Restart timer with new settings
    get().stopAutosaveTimer();
    if (newSettings.enabled && state.currentProject) {
      get().startAutosaveTimer();
    }
  },

  startAutosaveTimer: () => {
    const state = get();
    if (!state.autosaveSettings.enabled || !state.currentProject) return;
    
    // Clear existing timer
    if (autosaveTimer) {
      clearInterval(autosaveTimer);
    }
    
    // Start new timer
    autosaveTimer = setInterval(() => {
      const currentState = get();
      if (currentState.currentProject && currentState.autosaveSettings.enabled) {
        currentState.saveProject();
      }
    }, AUTOSAVE_INTERVAL_MS);
  },

  stopAutosaveTimer: () => {
    if (autosaveTimer) {
      clearInterval(autosaveTimer);
      autosaveTimer = null;
    }
  },

  updateFeatureFlags: (flags) => {
    const state = get();
    const newFlags = { ...state.featureFlags, ...flags };
    set({ featureFlags: newFlags });
    persistDesignerPrefsFromState(get());
  },

  resetFeatureFlags: () => {
    const defaultFlags = {
      enableCustomTemplates: false,
      showBetaBadges: true,
      enableAdvancedCableRun: false,
    };
    set({ featureFlags: defaultFlags });
    persistDesignerPrefsFromState(get());
  },
  
  // Port system actions
  setSelectedEquipmentForPortView: (equipment) => set({ selectedEquipmentForPortView: equipment }),
  setPortVisualizationOpen: (open) => set({ isPortVisualizationOpen: open }),
  setSelectedEquipmentForEdit: (equipment) => set({ selectedEquipmentForEdit: equipment }),
  setEquipmentEditorOpen: (open) => set({ isEquipmentEditorOpen: open }),
  
  saveEquipmentModifications: (equipment) => {
    const state = get();
    set({
      equipmentModifications: {
        ...state.equipmentModifications,
        [equipment.id]: equipment
      }
    });
    
    // Save to localStorage
    try {
      localStorage.setItem(
        'netprobuilder-equipment-modifications',
        JSON.stringify(state.equipmentModifications)
      );
    } catch (error) {
      console.error('Failed to save equipment modifications:', error);
    }
  },
  
  resetEquipmentModifications: (equipmentId) => {
    const state = get();
    const newModifications = { ...state.equipmentModifications };
    delete newModifications[equipmentId];
    
    set({ equipmentModifications: newModifications });
    
    // Update localStorage
    try {
      localStorage.setItem(
        'netprobuilder-equipment-modifications',
        JSON.stringify(newModifications)
      );
    } catch (error) {
      console.error('Failed to save equipment modifications:', error);
    }
  },
  
  getEditableEquipment: (equipmentId) => {
    const state = get();
    const modifications = state.equipmentModifications[equipmentId];
    
    // Return modified equipment or create default editable equipment
    return modifications || {
      id: equipmentId,
      name: 'Equipment',
      type: 'switch',
      manufacturer: 'ubiquiti',
      baseSpecifications: {},
      isCustom: false,
      isModified: false
    };
  },
  
  // Equipment customization actions
  saveEquipmentConfig: (equipmentId, config, options) => {
    const state = get();
    // Deep clone the config to avoid reference issues
    const clonedConfig = JSON.parse(JSON.stringify(config));
    const customizations = {
      ...state.equipmentCustomizations,
      [equipmentId]: { config: clonedConfig }
    };
    set({ equipmentCustomizations: customizations });
    saveEquipmentCustomizations(customizations);
    persistWorkingCopyNow(get());

    if (options?.updateExistingInstances !== false) {
      get().updateAllEquipmentInstances(equipmentId, config);
    }
  },
  
  // Get saved config for an equipment type (includes layout and other customizations)
  getEquipmentConfig: (equipmentId) => {
    const state = get();
    const saved = state.equipmentCustomizations[equipmentId];
    const config = saved?.config || null;
    // Deep clone to avoid reference issues
    return config ? JSON.parse(JSON.stringify(config)) : null;
  },
  
  // Clear saved config for an equipment type
  clearEquipmentConfig: (equipmentId) => {
    const state = get();
    const customizations = { ...state.equipmentCustomizations };
    delete customizations[equipmentId];
    set({ equipmentCustomizations: customizations });
    saveEquipmentCustomizations(customizations);
  },
  
  // Update all existing instances of an equipment type with new config
  updateAllEquipmentInstances: (equipmentId, config) => {
    const state = get();
    if (!state.currentProject) return;

    const project = ensureProjectHasDesigns(state.currentProject);
    const nextLayout = config?.layout;

    const updatedDesigns = (project.designs || []).map((d) => {
      let nextCables = uniqueCablesById(d.cables || []);

      const patchPlaced = (eq: PlacedEquipment): PlacedEquipment => {
        if (eq.equipment.id !== equipmentId) return eq;
        if (nextLayout) {
          const remaps = buildPortNumberRemaps(eq.config?.layout, nextLayout);
          nextCables = nextCables.map((cable) => {
            const from = rewritePortEndpoint(cable.from, eq.id, remaps, {
              nextLayout,
              detachOrphans: true,
            });
            const to = rewritePortEndpoint(cable.to, eq.id, remaps, {
              nextLayout,
              detachOrphans: true,
            });
            if (from === cable.from && to === cable.to) return cable;
            return { ...cable, from, to };
          });
        }
        const remappedLabels = nextLayout
          ? remapPortCustomLabels(
              eq.config?.portCustomLabels,
              buildPortNumberRemaps(eq.config?.layout, nextLayout)
            )
          : eq.config?.portCustomLabels;
        return {
          ...eq,
          config: {
            ...(eq.config || {}),
            ...config,
            ...(remappedLabels !== undefined ? { portCustomLabels: remappedLabels } : {}),
          },
        };
      };

      const nextEquipment = (d.equipment || []).map(patchPlaced);
      const nextRacks = (d.racks || []).map((rack) => ({
        ...rack,
        equipment: rack.equipment.map((rackEq) =>
          rackEq.equipment.id === equipmentId
            ? {
                ...rackEq,
                config: { ...(rackEq.config || {}), ...config },
              }
            : rackEq
        ),
      }));
      return { ...d, equipment: nextEquipment, racks: nextRacks, cables: nextCables, updatedAt: new Date() };
    });

    const selectedCableId = state.selectedCable?.id;
    const flatCables = updatedDesigns.flatMap((d) => d.cables || []);
    const updatedSelectedCable = selectedCableId
      ? flatCables.find((c) => c.id === selectedCableId) || state.selectedCable
      : state.selectedCable;

    set({
      currentProject: {
        ...project,
        designs: updatedDesigns,
        equipment: updatedDesigns[0]?.equipment ?? project.equipment,
        racks: updatedDesigns[0]?.racks ?? project.racks,
        updatedAt: new Date(),
      },
      selectedCable: updatedSelectedCable,
    });
    persistWorkingCopyNow(get());
  }
}));

registerCanvasCollabRuntime({
  save: (generation) => useProjectStore.getState().saveProjectForCollab(generation),
  isDragActive: () => useProjectStore.getState().isEquipmentDragActive,
  persistWorkingCopy: () => useProjectStore.getState().persistWorkingCopy(false),
});
useCanvasCollabStore.getState().setMode(useProjectStore.getState().canvasCollabMode);

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    if (workingCopyTimer) {
      clearTimeout(workingCopyTimer);
      workingCopyTimer = null;
    }
    persistWorkingCopyNow(useProjectStore.getState());
  });
}
